import { useState, useCallback, useRef, useEffect } from 'react';
// RC-118b — per-op label derivation reuses the same canvas-aware diff
// function the Versions slideout uses for cross-version comparison.
import { deriveOpLabel } from '../utils/canvas/diffDefinitions';
import { getNodePorts as getSoarNodePorts } from '../data/soarNodes';
import { layoutDAG, validateCanvasJSON, anchorCommentMarkers, alignJoinInboundBranches, pinJoinsToSplitterTrunk } from '../utils/dagLayout';
// ELK-based layered layout — fork/join spine-aware, orthogonal edge routing,
// obstacle avoidance. Runs async in a Web Worker. See utils/elkLayout.js.
import { elkLayoutDAG } from '../utils/elkLayout';
// IR pipeline (flatToIR → layoutIR → irToFlat) behind the `irPipeline` flag.
// When off (default), `runLayout` resolves to `layoutDAG` verbatim. See
// utils/ir/* and utils/irAdapter.js. Step 8 of the IR build.
import { layoutDAGviaIR } from '../utils/irAdapter';
import { isIRPipelineEnabled } from '../utils/featureFlags';
// Canonical, alias-aware type predicates — the same owner the IR walker uses,
// so "is this where flow ends" cannot mean one thing here and another there.
import { isTerminateType, isEndType } from '../utils/ir/kinds';

/**
 * Pick between the IR pipeline and the legacy Sugiyama layout based on the
 * runtime feature flag. Falls back to `layoutDAG` when IR returns null
 * (pipeline error on malformed input). Signature matches `layoutDAG(nodes,
 * connections, opts)` plus an optional `irLaidRef` — a React ref whose
 * `.current` is assigned the laid-out IR tree after a successful IR pass,
 * enabling the step 8.4 height-feedback hook.
 *
 * @param {Array} nodes
 * @param {Array} connections
 * @param {object} [opts]
 * @param {{current: object|null}} [irLaidRef]
 * @returns {Array}  Array of nodes with {x,y} set.
 */
function runLayout(nodes, connections, opts, irLaidRef) {
  if (isIRPipelineEnabled()) {
    const irOpts = irLaidRef
      ? { ...(opts || {}), onLaid: (w) => { irLaidRef.current = w; } }
      : opts;
    const positioned = layoutDAGviaIR(nodes, connections, irOpts);
    if (positioned != null) return positioned;
    // Pipeline declined (malformed / unsupported canvas) — fall through.
  }
  return layoutDAG(nodes, connections, opts);
}

// Conductor OSS renders simple-task nodes at 350 × 100 px. Adopting the same
// footprint so our Canvas matches their proportions out of the box.
const NODE_W = 350;
const NODE_H = 100;
const GRID_SIZE = 20;
const H_GAP = 90;   // 350 + 90 = 440 step — close to Conductor OSS's ~436 fork width
const V_GAP = 70;   // Conductor OSS-measured task-to-task visible gap

// =========================================================================
// Shared Shape Geometry — single source of truth for node shapes.
// All values in CANVAS coordinates (multiply by zoom for screen coords).
// Both useCanvas.js (port positions) and CanvasPage.jsx (rendering) use these.
// =========================================================================

const SHAPE_TYPES_MAP = {
  diamond: new Set(['condition', 'switch', 'decision', 'switch_case']),
  circle: new Set(['manual_start', 'end']),           // Non-interactive Conductor OSS-style circle labels
  bookend: new Set(['terminate']),                     // Terminate keeps bookend pill (interactive task)
  rect: new Set(['convergence']),
};

function getNodeShapeType(nodeType) {
  if (SHAPE_TYPES_MAP.diamond.has(nodeType)) return 'diamond';
  if (SHAPE_TYPES_MAP.circle.has(nodeType)) return 'circle';
  if (SHAPE_TYPES_MAP.bookend.has(nodeType)) return 'bookend';
  return 'rect';
}

// Per-shape geometry in canvas coordinates.
// Each shape defines where its visual surface is relative to the node's (x, y) origin.
const SHAPE_GEOM = {
  rect: {
    topY:    (ny) => ny,
    bottomY: (ny) => ny + NODE_H,
    centerX: (nx) => nx + NODE_W / 2,
    centerY: (ny) => ny + NODE_H / 2,
    halfW: NODE_W / 2,
  },
  bookend: {
    PILL_RATIO: 0.55,
    topY:    (ny) => ny + (NODE_H * (1 - 0.55)) / 2,
    bottomY: (ny) => ny + NODE_H - (NODE_H * (1 - 0.55)) / 2,
    centerX: (nx) => nx + NODE_W / 2,
    centerY: (ny) => ny + NODE_H / 2,
    halfW: NODE_W / 2,
    pillH: NODE_H * 0.55,
  },
  circle: {
    // Small Conductor OSS-style circle for Start/End labels — non-interactive markers.
    // Circle radius = 18px in canvas coords. Text label sits below the circle.
    RADIUS: 18,
    topY:    (ny) => ny + NODE_H / 2 - 18,
    bottomY: (ny) => ny + NODE_H / 2 + 18,
    centerX: (nx) => nx + NODE_W / 2,
    centerY: (ny) => ny + NODE_H / 2,
    halfW: 18,
  },
  diamond: {
    // Diamond is visually TALLER than the bounding box for proper proportions.
    // Half-height = 50 means diamond extends 14px above and below the 72px box.
    // This still fits within V_GAP=120 between layout rows.
    HALF_W: NODE_W * 0.48,   // 96px — horizontal extent
    HALF_H: 50,              // 50px — vertical extent (vs 34.6 if constrained to NODE_H)
    topY:    (ny) => ny + NODE_H / 2 - 50,
    bottomY: (ny) => ny + NODE_H / 2 + 50,
    centerX: (nx) => nx + NODE_W / 2,
    centerY: (ny) => ny + NODE_H / 2,
    halfW: NODE_W * 0.48,
  },
};

// Export for CanvasPage.jsx to use
export { NODE_W, NODE_H, SHAPE_GEOM, getNodeShapeType };

// Trigger node types — recognized in palette + IR fallback findStart tiebreaker.
const TRIGGER_TYPES = new Set([
  'manual_start', 'manual_trigger', 'webhook', 'schedule', 'schedule_trigger',
  'siem_alert', 'email_trigger', 'trigger',
]);

/* Phase B-fix.3 — Canonical entry sentinels ONLY. These nodes MUST have no
 * incoming edges (multi-Start would crash flatToIR). Other trigger-type nodes
 * (schedule, webhook, siem_alert, email_trigger) are regular addable nodes
 * post-Phase-B-fix.3 — they CAN have incoming edges from manual_start. The
 * pre-Phase-B-fix.3 logic used the full TRIGGER_TYPES set here and stripped
 * the start-1 → trigger-1 edge from system workflows seeded with the new
 * canonical 4-node DAG (manual_start → schedule → ... → audit_event), which
 * regressed to a parallel fan-out from start-1 to every other node.
 * See SYSTEM_WORKFLOW_RULES.md §2.6. */
const ENTRY_SENTINEL_TYPES = new Set(['manual_start', 'start']);

/**
 * Ports that carry a DESIGNED BACK-EDGE. These are not accidental cycles — they are how two
 * first-class execution shapes are expressed on the canvas:
 *
 *   • `loop_back`        — Do-While iteration back-edge (body tail → loop head).
 *   • `cognitive_return` — cognitive-loop tool return edge (tool → cognitive_loop), per
 *                          AGENT_EXECUTION_MODEL.md §4/§7. A cognitive agent's graph IS a
 *                          reasoning loop: cognitive_loop → tool → cognitive_loop.
 *
 * Mirrors the canonical `AUX_SOURCE_HANDLES` in utils/ir/flatToIR.js (which excludes them from
 * the structural trunk walk) and `BACK_EDGE_PORTS` in the backend WorkflowDraftValidator (which
 * excludes them from acyclicity). validateAndRepairDAG must honour the SAME contract: without
 * it, Kahn's sort below can't order a cognitive/Do-While graph, every downstream node is
 * misread as a "cycle node", and the repair strips real trunk edges (e.g. cognitive_loop → end)
 * and then fabricates parallel orphan edges — silently demolishing the graph on load.
 *
 * NOT included: `cognitive_avail` (cognitive_loop → tool) is a FORWARD edge and must stay in the
 * graph so fan-out tools remain reachable (else Phase 4 re-parents them as orphans).
 */
const BACK_EDGE_PORTS = new Set(['loop_back', 'cognitive_return']);
const isBackEdge = (c) => BACK_EDGE_PORTS.has(c?.fromPort || c?.sourcePort);

/**
 * Edge identity — what makes two connections "the same edge".
 *
 * <p>The ports are part of it. `canConnect` has always known that: it rejects
 * a new connection only when all four of from/fromPort/to/toPort match, which
 * is what makes a switch with two cases converging on one node a legal and
 * ordinary shape.
 *
 * The duplicate-removal phase disagreed. It keyed on `from → to` alone, so on
 * every load it deleted one of
 *
 *     switch --APPROVE--> audit
 *     switch --DENY-----> audit
 *
 * whichever came second in the array. That branch's port went dead, the
 * branch stopped executing, and the deletion was persisted on the next save.
 * The editor let you draw it and the loader quietly took it away.
 *
 * Both callers now share this one definition, so they cannot drift apart
 * again. Ports are normalized because an absent port and an explicit
 * 'default' are the same edge — without that, a genuine duplicate written two
 * ways would survive deduplication, which is the opposite mistake.
 *
 * Deliberately NOT collapsing auxiliary handles: `cognitive_avail` and
 * `cognitive_return` between the same pair of nodes are two different edges
 * in opposite directions, and a Do-While's body and `loop_back` are too.
 */
const portOf = (p) => (p == null || p === '' ? 'default' : String(p));
const edgeKey = (c) =>
  `${c?.from}|${portOf(c?.fromPort)}→${c?.to}|${portOf(c?.toPort)}`;

/**
 * validateAndRepairDAG — enforces strict DAG (Directed Acyclic Graph) rules.
 *
 * 1. Removes self-loops and connections referencing non-existent nodes
 * 2. Detects and removes cycle-forming connections (back-edges)
 * 3. Reconnects orphan nodes that aren't reachable from the trigger
 * 4. Ensures trigger has no incoming connections
 *
 * Returns { nodes, connections, repairs[] } — repairs is empty if DAG was already valid.
 */
export function validateAndRepairDAG(nodes, connections) {
  const repairs = [];
  const nodeIds = new Set(nodes.map((n) => n.id));

  /* --- Phase 0: Report duplicate node ids ---------------------------------
   *
   * Two nodes sharing an id collapse into one everywhere downstream and did
   * so in total silence. `nodeIds` above is a Set, and flatToIR's node map is
   * built with `ctx.nodes.set(n.id, n)` — last write wins. The shadowed node
   * survives in the saved JSON and in the Code panel, so the payload and the
   * canvas disagree about how many steps the workflow has, and the operator
   * has no way to tell: the node they are looking for is simply not drawn and
   * nothing says why.
   *
   * This reports rather than resolves, deliberately. Ids must be unique, so
   * the two nodes cannot both survive as authored — and there is no correct
   * automatic answer to which one the author meant. Renaming one invents an
   * identity the operator never chose; dropping one destroys work; and any
   * edge pointing at that id is genuinely ambiguous between the two, so
   * rewiring is guesswork too. A repair that guesses here would be the
   * canvas rewriting the operator's workflow into a shape they did not
   * author, which is the class of defect this whole remediation is about.
   *
   * What was wrong was the silence. Naming the collision — with the id and
   * the labels involved, so the operator can find both in the Code panel —
   * is the honest fix, and it leaves the decision with the person who knows
   * which one they meant.
   */
  if (nodeIds.size !== nodes.length) {
    const seen = new Map();
    for (const n of nodes) {
      if (!n || !n.id) continue;
      const bucket = seen.get(n.id);
      if (bucket) bucket.push(n);
      else seen.set(n.id, [n]);
    }
    for (const [id, group] of seen) {
      if (group.length < 2) continue;
      const labels = group
        .map((n) => (typeof n.label === 'string' && n.label.trim()) || n.type || 'untitled')
        .join('", "');
      repairs.push(
        `Duplicate node id "${id}" is used by ${group.length} steps ("${labels}"). ` +
        'Only one of them can appear on the canvas. Give each step its own id in ' +
        'the Code panel to make them all visible.',
      );
    }
  }

  if (process.env.NODE_ENV !== 'production') {
    console.log(`[validateAndRepairDAG] Input: ${nodes.length} nodes, ${connections.length} connections`);
  }

  // --- Phase 1: Remove invalid connections (self-loops, missing node refs) ---
  let cleanConns = connections.filter((c) => {
    if (c.from === c.to) {
      repairs.push(`Removed self-loop on ${c.from}`);
      return false;
    }
    if (!nodeIds.has(c.from) || !nodeIds.has(c.to)) {
      repairs.push(`Removed connection with missing node: ${c.from} → ${c.to}`);
      return false;
    }
    return true;
  });

  if (process.env.NODE_ENV !== 'production' && cleanConns.length < connections.length) {
    console.log(`[validateAndRepairDAG] Phase 1: ${connections.length} → ${cleanConns.length} connections`);
  }

  // --- Phase 2: Remove incoming connections to canonical entry sentinels ---
  // Phase B-fix.3 — scope narrowed from TRIGGER_TYPES (8 types) to
  // ENTRY_SENTINEL_TYPES (2 types). Only manual_start / start can never have
  // incoming edges (multi-Start crashes flatToIR). Other trigger-capable
  // nodes (schedule / webhook / siem_alert / email_trigger / system_event)
  // are regular addable nodes and CAN sit downstream of manual_start.
  const entrySentinelIds = new Set(nodes.filter((n) => ENTRY_SENTINEL_TYPES.has(n.type)).map((n) => n.id));
  const prePhase2 = cleanConns.length;
  cleanConns = cleanConns.filter((c) => {
    if (entrySentinelIds.has(c.to)) {
      repairs.push(`Removed incoming connection to entry sentinel: ${c.from} → ${c.to}`);
      return false;
    }
    return true;
  });
  if (process.env.NODE_ENV !== 'production' && cleanConns.length < prePhase2) {
    console.log(`[validateAndRepairDAG] Phase 2 (trigger cleanup): ${prePhase2} → ${cleanConns.length} connections`);
  }

  // --- Phase 3: Detect and remove cycle-forming connections ---
  // Designed back-edges (loop_back / cognitive_return) are EXCLUDED from the cycle math and
  // are never removal candidates — they are preserved verbatim in cleanConns. Only accidental
  // cycles on structural edges are repaired. See BACK_EDGE_PORTS above.
  for (let iter = 0; iter < 10; iter++) {
    const adj = new Map();
    const indegree = new Map();
    nodeIds.forEach((id) => { adj.set(id, []); indegree.set(id, 0); });
    cleanConns.forEach((c) => {
      if (isBackEdge(c)) return; // designed back-edge — not part of the DAG order
      adj.get(c.from).push(c.to);
      indegree.set(c.to, (indegree.get(c.to) || 0) + 1);
    });

    const queue = [];
    nodeIds.forEach((id) => { if (indegree.get(id) === 0) queue.push(id); });
    const sorted = [];
    while (queue.length > 0) {
      const curr = queue.shift();
      sorted.push(curr);
      (adj.get(curr) || []).forEach((neighbor) => {
        indegree.set(neighbor, indegree.get(neighbor) - 1);
        if (indegree.get(neighbor) === 0) queue.push(neighbor);
      });
    }

    if (sorted.length === nodeIds.size) break; // No cycles

    // Find cycle nodes and remove the last cycle-forming STRUCTURAL edge.
    const sortedSet = new Set(sorted);
    const cycleNodes = new Set([...nodeIds].filter((id) => !sortedSet.has(id)));
    let removed = false;
    for (let i = cleanConns.length - 1; i >= 0; i--) {
      if (isBackEdge(cleanConns[i])) continue; // never sacrifice a designed back-edge
      if (cycleNodes.has(cleanConns[i].from) && cycleNodes.has(cleanConns[i].to)) {
        repairs.push(`Removed cycle-forming connection: ${cleanConns[i].from} → ${cleanConns[i].to}`);
        cleanConns.splice(i, 1);
        removed = true;
        break;
      }
    }
    // Nothing left to safely remove — stop rather than loop 10x doing nothing.
    if (!removed) break;
  }

  // --- Phase 3b: Deduplicate connections (identical edge, ports included) ---
  {
    const seen = new Set();
    const beforeDedup = cleanConns.length;
    cleanConns = cleanConns.filter((c) => {
      const key = edgeKey(c);
      if (seen.has(key)) {
        repairs.push(
          `Removed duplicate connection: ${c.from} → ${c.to} (${portOf(c.fromPort)})`,
        );
        return false;
      }
      seen.add(key);
      return true;
    });
    if (cleanConns.length < beforeDedup) {
      console.warn(`[validateAndRepairDAG] Removed ${beforeDedup - cleanConns.length} duplicate connections`);
    }
  }

  // --- Phase 4: Reconnect orphan nodes to nearest upstream ---
  // CRITICAL: All orphans are connected to the SAME parent node (in parallel),
  // NOT chained sequentially. Sequential chaining converts parallel topology to serial.
  //
  // NOTE: G443 introduced a band-aid that re-anchored orphans at the TRIGGER instead
  // of the topo-tail, but per the V1↔V2 redesign ground rules anti-pattern checklist
  // that swap broke workflows whose layout previously worked under the topo-tail logic.
  // This block is the baseline pre-G443 implementation preserved during cherry-pick #9.
  //
  // Phase B-fix.3 — anchor at the ENTRY SENTINEL (manual_start), not the first
  // trigger-capable node. Mid-DAG trigger nodes (schedule / webhook / etc.) used
  // to be the anchor under the old logic, which fan-out-attached all orphans
  // directly to a Schedule / Webhook node and bypassed manual_start entirely.
  const triggerId = nodes.find((n) => ENTRY_SENTINEL_TYPES.has(n.type))?.id
                 ?? nodes.find((n) => TRIGGER_TYPES.has(n.type))?.id; // legacy fallback
  if (triggerId) {
    const adj = new Map();
    nodeIds.forEach((id) => adj.set(id, []));
    cleanConns.forEach((c) => adj.get(c.from).push(c.to));

    const reachable = new Set();
    const queue = [triggerId];
    reachable.add(triggerId);
    while (queue.length > 0) {
      const curr = queue.shift();
      (adj.get(curr) || []).forEach((neighbor) => {
        if (!reachable.has(neighbor)) { reachable.add(neighbor); queue.push(neighbor); }
      });
    }

    const orphans = [...nodeIds].filter((id) => !reachable.has(id));
    if (orphans.length > 0) {
      // Compute topological order of reachable nodes to find the best parent
      const topoOrder = computeTopoOrder(reachable, cleanConns);
      const parentNode = topoOrder.length > 0 ? topoOrder[topoOrder.length - 1] : triggerId;

      /* ── The anchor must not be somewhere the orphan can already reach ──
       *
       * An orphan is a node the TRIGGER cannot reach. That says nothing about
       * where the orphan itself leads, and the two are independent: a node can
       * be unreachable from the trigger while having perfectly good outgoing
       * edges of its own.
       *
       * Anchoring such a node at the topological tail closes a loop. Given
       *     S → n0, n0 → n3, n2 → n3, n2 → E
       * n2 is unreachable, the tail is n3, and attaching n3 → n2 creates a
       * 2-cycle with the n2 → n3 that was already there. The phase whose FIRST
       * job is removing cycles was manufacturing one, in 43% of generated
       * acyclic graphs.
       *
       * That is also why this repair never settled. The next load's Phase 3
       * removed the fabricated cycle, which re-orphaned the subtree, which this
       * phase re-anchored somewhere else — so the stored workflow was rewritten
       * into a different shape on every single open, with no edit from anyone.
       *
       * The fix is a constraint, not a special case: an anchor is valid only if
       * the orphan cannot reach it. Candidates are tried deepest-first, matching
       * the topological intent of the original choice, and the trigger is the
       * final fallback — provably safe, because Phase 2 strips every inbound
       * edge to the entry sentinel, so nothing in the graph can reach it.
       *
       * Back-edges are excluded from the walk for the same reason
       * computeTopoOrder excludes them: `loop_back` and `cognitive_return` are
       * designed cycles, and counting them would make every node downstream of
       * a loop look like it reaches the loop head.
       */
      const structuralAdj = new Map();
      nodeIds.forEach((id) => structuralAdj.set(id, []));
      cleanConns.forEach((c) => {
        if (!isBackEdge(c) && structuralAdj.has(c.from)) structuralAdj.get(c.from).push(c.to);
      });

      const canReach = (fromId, targetId) => {
        if (fromId === targetId) return true;
        const seen = new Set([fromId]);
        const stack = [fromId];
        while (stack.length > 0) {
          const curr = stack.pop();
          for (const next of structuralAdj.get(curr) || []) {
            if (next === targetId) return true;
            if (!seen.has(next)) { seen.add(next); stack.push(next); }
          }
        }
        return false;
      };

      /* ── An end or terminate node is never a valid anchor ──────────────
       *
       * The topological tail of a normal workflow IS the End node, so the
       * previous choice produced `end → orphan` on the most ordinary graph
       * there is. The render walk strips outbound edges from sentinels — they
       * are where flow stops — so that edge was discarded at draw time.
       *
       * The result was a repair that reported success and did nothing: the
       * banner said a structural issue was fixed, the node still did not
       * appear, and the useless edge was persisted on the next save. Worse,
       * it was self-concealing — on the following load the orphan scan walks
       * all connections, sees the edge, and no longer considers the node an
       * orphan at all. Stable, wrong, and invisible.
       *
       * The backend already knew this (RC-97g) and skips sentinel candidates
       * when picking its anchor. This is the frontend finally getting the
       * same filter. */
      const nodeTypeById = new Map(nodes.map((n) => [n.id, n.type]));
      const isFlowEnd = (id) => {
        const t = nodeTypeById.get(id);
        return isTerminateType(t) || isEndType(t);
      };

      /* Deepest reachable NON-SENTINEL node the orphan cannot get back to.
       * Falls back to the trigger, which no node can reach. */
      const anchorFor = (orphanId) => {
        if (!isFlowEnd(parentNode) && !canReach(orphanId, parentNode)) return parentNode;
        for (let i = topoOrder.length - 1; i >= 0; i--) {
          if (isFlowEnd(topoOrder[i])) continue;
          if (!canReach(orphanId, topoOrder[i])) return topoOrder[i];
        }
        return triggerId;
      };

      const nameOf = (id) => {
        const n = nodes.find((x) => x.id === id);
        return (n && (n.label || n.name)) || id;
      };

      // Orphans still share one parent wherever that is safe, so a graph that
      // repaired cleanly before repairs identically now — only an orphan that
      // would have closed a cycle, or that would have hung off a sentinel, is
      // routed elsewhere.
      orphans.forEach((orphanId) => {
        /* An unconnected end node is left alone rather than wired to the last
         * step. Wiring it would fork that step into two endings — an ending it
         * already has, plus this one — which is a different workflow from the
         * one on screen. It renders as unconnected, which is the truth, and
         * the operator is told so plainly. */
        if (isFlowEnd(orphanId)) {
          repairs.push(
            `"${nameOf(orphanId)}" isn't connected to anything yet. ` +
            `Draw a connection into it from the step that should finish there, or delete it.`,
          );
          return;
        }

        const anchorId = anchorFor(orphanId);
        const newConn = {
          id: `c_repair_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          from: anchorId,
          fromPort: 'default',
          to: orphanId,
          toPort: 'default',
        };
        cleanConns.push(newConn);
        // Keep the adjacency current: a later orphan must see this edge, or two
        // orphans wired in the same pass could still close a cycle between them.
        if (structuralAdj.has(anchorId)) structuralAdj.get(anchorId).push(orphanId);
        repairs.push(
          `Connected "${nameOf(orphanId)}" after "${nameOf(anchorId)}" — it had no incoming ` +
          `connection, so it could never have run.`,
        );
      });
    }
  }

    if (process.env.NODE_ENV !== 'production') {
    console.log(`[validateAndRepairDAG] Output: ${nodes.length} nodes, ${cleanConns.length} connections (${repairs.length} repairs)`);
    if (repairs.length > 0) {
      repairs.forEach((r) => console.log(`  repair: ${r}`));
    }
  }

  return { nodes, connections: cleanConns, repairs };
}

/**
 * Compute topological order for a set of node IDs given connections.
 */
function computeTopoOrder(nodeIds, connections) {
  const adj = new Map();
  const indegree = new Map();
  nodeIds.forEach((id) => { adj.set(id, []); indegree.set(id, 0); });
  connections.forEach((c) => {
    // Designed back-edges (loop_back / cognitive_return) don't participate in DAG ordering —
    // counting them would leave a cognitive/Do-While graph unsortable and yield a truncated
    // order (and thus a bogus "topo tail" parent for orphan re-parenting).
    if (isBackEdge(c)) return;
    if (nodeIds.has(c.from) && nodeIds.has(c.to)) {
      adj.get(c.from).push(c.to);
      indegree.set(c.to, (indegree.get(c.to) || 0) + 1);
    }
  });
  const queue = [];
  nodeIds.forEach((id) => { if (indegree.get(id) === 0) queue.push(id); });
  const result = [];
  while (queue.length > 0) {
    const curr = queue.shift();
    result.push(curr);
    (adj.get(curr) || []).forEach((neighbor) => {
      indegree.set(neighbor, indegree.get(neighbor) - 1);
      if (indegree.get(neighbor) === 0) queue.push(neighbor);
    });
  }
  return result;
}

/**
 * useCanvas — Governed Agent Orchestration canvas hook.
 * Comprehensive DAG management with multi-port support, execution state tracking,
 * and advanced operations like validation, topological sort, and auto-layout.
 */
export function useCanvas({ onSave } = {}) {
  // =========================================================================
  // Core State
  // =========================================================================
  const [nodes, setNodes] = useState([]);
  const [connections, setConnections] = useState([]);
  // RC-CanvasV3-ProtectManifest — exact-replay manifest set by the
  // Protect button when it inserts governance nodes. Read by the
  // Unprotect path so it can remove ONLY the nodes Protect added
  // and restore ONLY the edges Protect replaced — no inference, no
  // bypass logic, no fork pathology. Survives BE round-trip via the
  // workflow definition JSONB. Null when the canvas isn't protected
  // or was protected before this slice shipped.
  const [protectManifest, setProtectManifest] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedType, setSelectedType] = useState(null); // 'node' | 'connection' | 'multi'
  const [multiSelect, setMultiSelect] = useState(new Set());
  const [dragging, setDragging] = useState(null);
  const [connecting, setConnecting] = useState(null); // {fromId, fromPort, mouseX, mouseY}
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [dirty, setDirty] = useState(false);

  // Execution state: Map<nodeId, { status, output, error, startedAt, completedAt, durationMs }>
  const [executionState, setExecutionState] = useState(new Map());

  // DAG validation: { valid, errors, warnings, topologicalOrder }
  const [dagValidation, setDagValidation] = useState({
    valid: true,
    errors: [],
    warnings: [],
    topologicalOrder: [],
  });

  // P1-B: Surfaced to the UI as a yellow banner. The DAG validator already
  // repairs orphans / cycles / duplicate edges silently; this captures the
  // repair list from the most recent loadCanvasData call so the user can see
  // (and acknowledge) what the validator changed. Cleared by `dismissOrphanRepairReport()`
  // or implicitly on the next loadCanvasData with no repairs.
  const [orphanRepairReport, setOrphanRepairReport] = useState(null);

  // Data flow: Map<connectionId, { sourceVar, targetInput, dataPreview }>
  const [dataFlow, setDataFlow] = useState(new Map());

  const panRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const svgRef = useRef(null);

  /* RC-117c — Narrative hint for the NEXT save's change_kind.
   *
   * The persisted WorkflowVersion row records why the save happened
   * (manual_save / ai_build / ai_fix / import / rollback). Without this
   * hint, every AI-driven save was mis-labeled "manual_save" — the
   * Versions slideout couldn't distinguish "operator typed this" from
   * "AI rewrote it." The save callback reads this ref once and clears
   * it, so the hint is one-shot and never leaks to a later unrelated
   * save. Null → service default ("manual_save"). */
  const pendingChangeKindRef = useRef(null);
  const setPendingChangeKind = useCallback((kind) => {
    pendingChangeKindRef.current = kind || null;
  }, []);
  const consumePendingChangeKind = useCallback(() => {
    const k = pendingChangeKindRef.current;
    pendingChangeKindRef.current = null;
    return k;
  }, []);
  // Holds the most recent laid-out IR tree (step 8.4 height-feedback anchor).
  // Populated by `runLayout` via onLaid whenever the IR pipeline is active;
  // consumed by `useIRHeightFeedback` in CanvasPage.jsx.
  const irLaidRef = useRef(null);
  const captureIRLaid = useCallback((w) => { irLaidRef.current = w; }, []);

  const markDirty = useCallback(() => setDirty(true), []);

  // =========================================================================
  // RC-118 — Undo / Redo (history substrate)
  // =========================================================================
  //
  // Coarse-grained snapshot model: each entry in `pastRef` is a deep-cloned
  // {nodes, connections} pair captured AFTER a mutation commits. This trades
  // memory for simplicity — a 50-node canvas is ~50KB JSON, the 50-entry cap
  // bounds total cost at ~2.5MB worst case. Per-operation deltas would halve
  // that but add ~5x the wire complexity for no operator-facing benefit.
  //
  // **Why a useEffect-driven commit instead of hooking into every mutator:**
  // The canvas has 30+ mutation sites (addNode, updateNode, deleteNode,
  // insertNodeOnConnection, appendNodeAfter, addConnection, deleteConnection,
  // moveNode, multi-select-move, drag-drop, recipe-apply, auto-repair…). Each
  // already calls `setNodes` / `setConnections` and `markDirty`. A useEffect
  // on `[nodes, connections]` runs AFTER React commits the new state — so we
  // capture the post-mutation snapshot from one seam regardless of which
  // mutator ran. Hooking every mutator individually was the alternative; it
  // ships 30+ touch points instead of one, and is structurally guaranteed to
  // miss future mutators someone adds without remembering to commit.
  //
  // **Debounce:** rapid mutations (dragging a node, typing in a field,
  // multi-cell paste) shouldn't flood the stack — operators expect a single
  // Cmd+Z to undo "the drag", not 60 individual frames. The DEBOUNCE_MS
  // window collapses consecutive commits into a single entry by replacing
  // the latest rather than appending.
  //
  // **Replay suppression:** undo() / redo() / resetHistory() set
  // `isReplayingRef` so the next nodes/connections useEffect tick skips the
  // commit — otherwise undo would push its own restore as a new history
  // entry, and redo would be lost.
  //
  // **Reset semantics:** loadCanvasData / clear-canvas reset history to a
  // single baseline snapshot. Undoing past a load would unload the workflow
  // the operator just opened, which is never the intent.
  //
  // **Selection / pan / zoom / execution state are deliberately NOT in the
  // snapshot.** Selection isn't canvas content; pan/zoom are view state;
  // execution state is runtime telemetry. Undo should change WHAT the
  // workflow does, not what the operator is currently looking at.
  //
  // Phase 2 (RC-118b) ideas: localStorage-backed persistence so undo
  // survives reloads; finer-grained operation labels in the toolbar
  // ("Undo Delete node 'foo'"); branch-aware history.
  const HISTORY_CAP = 50;
  const HISTORY_DEBOUNCE_MS = 500;
  const pastRef    = useRef([]);    // [{nodes, connections}, ...] oldest first
  const futureRef  = useRef([]);    // [{nodes, connections}, ...] for redo
  const lastCommitAtRef = useRef(0);
  const isReplayingRef  = useRef(false);  // true during undo/redo/reset
  const isInitialCaptureRef = useRef(false); // true between mount and first commit
  // `historyTick` exists ONLY to force a re-render of toolbar consumers when
  // canUndo / canRedo change. The actual stacks live in refs because we never
  // need React to re-render on every mutation — only the toolbar enabled-
  // state needs to know "can undo now? can redo now?".
  const [historyTick, setHistoryTick] = useState(0);

  /**
   * Deep-clone a {nodes, connections} snapshot. Avoids structuredClone
   * because IE11 / older Electron embeds don't have it; the canvas runs
   * in those targets via the agentlets-desktop wrapper.
   */
  const cloneSnapshot = useCallback((srcNodes, srcConns) => ({
    nodes: srcNodes.map((n) => ({
      ...n,
      config: n.config ? JSON.parse(JSON.stringify(n.config)) : {},
    })),
    connections: srcConns.map((c) => ({ ...c })),
  }), []);

  /**
   * Drop the entire history and seed it with a single baseline snapshot.
   * Called when loadCanvasData lands a new workflow (the freshly-loaded
   * state IS the baseline; undo should never roll back past it).
   */
  const resetHistory = useCallback(() => {
    pastRef.current   = [];          // populated by the next useEffect tick
    futureRef.current = [];
    lastCommitAtRef.current = 0;
    isReplayingRef.current = true;   // suppress the next auto-commit so
                                     // the baseline lands via the
                                     // post-load useEffect tick, not as
                                     // a "mutation"
    isInitialCaptureRef.current = true;
    setHistoryTick((t) => t + 1);
  }, []);

  // Auto-commit after every nodes/connections change. The `isReplayingRef`
  // guard prevents undo/redo/reset from cascading into more commits.
  //
  // RC-118b extension: each snapshot is now an object
  //   { nodes, connections, label }
  // where `label` is the operator-readable summary of the mutation that
  // produced THIS snapshot (derived from the diff between the prior
  // snapshot and this one). The label drives the toolbar tooltip and
  // — on redo — tells the operator what they'd be re-applying. Baseline
  // and replay-restore entries get label='' so the tooltip falls back to
  // a generic "Undo" / "Redo" string in the toolbar.
  useEffect(() => {
    if (isReplayingRef.current) {
      isReplayingRef.current = false;
      // If we're servicing a reset, ALSO seed the baseline snapshot here
      // (because the reset cleared past[]). Otherwise this is an undo/redo
      // restore and the stacks are already correct.
      if (isInitialCaptureRef.current) {
        isInitialCaptureRef.current = false;
        pastRef.current = [{
          ...cloneSnapshot(nodes, connections),
          label: '', // baseline — no preceding mutation to describe
        }];
        lastCommitAtRef.current = Date.now();
        setHistoryTick((t) => t + 1);
      }
      return;
    }
    const now = Date.now();
    const snap = cloneSnapshot(nodes, connections);
    // RC-118b — derive label from the delta vs the prior entry (or
    // empty when this IS the very first non-baseline entry, no prior
    // exists yet). deriveOpLabel is failure-soft; never throws.
    const prior = pastRef.current.length > 0
      ? pastRef.current[pastRef.current.length - 1]
      : null;
    const label = prior ? deriveOpLabel(prior, snap) : '';
    const entry = { ...snap, label };
    // Debounce: replace-latest when commits arrive within DEBOUNCE window.
    // The label of a replaced entry is RE-derived against the same prior
    // so a drag (many commits, one undoable group) is labeled by the
    // delta between drag-start and drag-end — not by the last 16ms.
    if (pastRef.current.length > 0
        && now - lastCommitAtRef.current < HISTORY_DEBOUNCE_MS) {
      // Re-derive against the entry BEFORE the one we're about to replace,
      // not against the soon-to-be-clobbered entry itself.
      const priorPrior = pastRef.current.length > 1
        ? pastRef.current[pastRef.current.length - 2]
        : null;
      entry.label = priorPrior ? deriveOpLabel(priorPrior, snap) : '';
      pastRef.current[pastRef.current.length - 1] = entry;
    } else {
      pastRef.current.push(entry);
      if (pastRef.current.length > HISTORY_CAP) {
        pastRef.current.shift();
      }
    }
    // Any new mutation invalidates the redo stack.
    if (futureRef.current.length > 0) {
      futureRef.current = [];
    }
    lastCommitAtRef.current = now;
    setHistoryTick((t) => t + 1);
  }, [nodes, connections, cloneSnapshot]);

  const undo = useCallback(() => {
    // Need at least the baseline + one mutation to undo; baseline-only =
    // no-op (no prior state to restore).
    if (pastRef.current.length < 2) return;
    const current = pastRef.current.pop();
    futureRef.current.push(current);
    const prev = pastRef.current[pastRef.current.length - 1];
    isReplayingRef.current = true;
    setNodes(prev.nodes);
    setConnections(prev.connections);
    markDirty();
    setHistoryTick((t) => t + 1);
  }, [markDirty]);

  const redo = useCallback(() => {
    if (futureRef.current.length === 0) return;
    const next = futureRef.current.pop();
    pastRef.current.push(next);
    isReplayingRef.current = true;
    setNodes(next.nodes);
    setConnections(next.connections);
    markDirty();
    setHistoryTick((t) => t + 1);
  }, [markDirty]);

  /**
   * Derived flags. Recomputed every render that historyTick bumps, which
   * happens on every commit/undo/redo. Cheap O(1) length checks; no need
   * to memoise.
   */
  const canUndo = pastRef.current.length >= 2;
  const canRedo = futureRef.current.length > 0;
  /* RC-118b — labels for the next undoable / redoable action. Drives
   * toolbar tooltips ("Undo Add node 'X'" instead of bare "Undo"). The
   * undoLabel is the label of the LATEST past entry (= the mutation
   * an undo would reverse). The redoLabel is the label of the entry
   * an undo most recently put onto future[] (= the mutation a redo
   * would re-apply). Empty strings ('') are valid — the toolbar falls
   * back to a generic affordance string in that case. */
  const undoLabel = canUndo
    ? (pastRef.current[pastRef.current.length - 1]?.label || '')
    : '';
  const redoLabel = canRedo
    ? (futureRef.current[futureRef.current.length - 1]?.label || '')
    : '';

  // =========================================================================
  // RC-118b — localStorage persistence
  // =========================================================================
  //
  // Goal: undo survives a tab close / page reload so the operator's
  // last hour of edits isn't lost to an accidental refresh. RC-118
  // Phase 1 shipped in-memory only; this extension adds opt-in
  // persistence keyed by workflowId.
  //
  // Constraints:
  //   * localStorage cap is ~5MB browser-wide. A 50-node canvas
  //     snapshot is ~50KB; a full 50-entry stack = ~2.5MB per
  //     workflow — too much. PERSIST_CAP = 10 entries keeps each
  //     workflow under ~500KB; the in-memory stack stays at 50.
  //   * Cross-workflow contamination would be a nightmare (Cmd+Z
  //     unloads someone else's edits). Restore only when the persisted
  //     baseline signature matches the freshly-loaded state — otherwise
  //     drop the persisted data on the floor.
  //   * Failures here MUST be soft. localStorage throws in private-
  //     window mode and when quota is exceeded; we degrade silently to
  //     in-memory-only behavior (= pre-RC-118b).
  const PERSIST_CAP = 10;
  const STORAGE_PREFIX = 'agentlets.canvas.undo.';

  /**
   * Compact signature of a snapshot for baseline-match checking.
   * NOT a security hash — just a quick fingerprint to detect "this
   * persisted stack belongs to a different state of the workflow than
   * what we just loaded." Uses sorted-keys JSON length + node-id list.
   */
  const snapshotSig = useCallback((snap) => {
    if (!snap) return '';
    const nodeIds = (snap.nodes || []).map((n) => n && n.id).filter(Boolean).sort().join(',');
    const connIds = (snap.connections || []).map((c) => c && c.id).filter(Boolean).sort().join(',');
    return `${snap.nodes?.length || 0}n:${snap.connections?.length || 0}c:${nodeIds.length}:${connIds.length}`;
  }, []);

  /**
   * Restore persisted history for `workflowId` if-and-only-if the
   * persisted baseline matches the in-memory baseline. No-op when
   * localStorage is unavailable or the persisted blob is missing /
   * stale / corrupt / for a different workflow shape.
   */
  const rehydrateHistoryFor = useCallback((workflowId) => {
    if (!workflowId) return false;
    try {
      const raw = window.localStorage.getItem(STORAGE_PREFIX + workflowId);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.past) || !Array.isArray(parsed.future)) return false;
      // Baseline-match check: the in-memory pastRef should already have
      // the freshly-loaded baseline (loadCanvasData → resetHistory →
      // useEffect commits baseline) by the time CanvasPageV3 calls this
      // hook. If the persisted baseline doesn't match, drop the blob.
      const inMemBaseline = pastRef.current[0];
      const persistedBaseline = parsed.past[0];
      if (!inMemBaseline || !persistedBaseline) return false;
      if (snapshotSig(inMemBaseline) !== snapshotSig(persistedBaseline)) {
        // Stale snapshot from before the workflow was edited externally
        // (e.g. another tab saved). Drop it rather than risking an undo
        // that unloads someone else's edits.
        window.localStorage.removeItem(STORAGE_PREFIX + workflowId);
        return false;
      }
      pastRef.current = parsed.past;
      futureRef.current = parsed.future;
      setHistoryTick((t) => t + 1);
      return true;
    } catch {
      // Any failure → degrade to in-memory-only. localStorage may be
      // disabled (private-browsing) / quota-exceeded / blob corrupt.
      return false;
    }
  }, [snapshotSig]);

  /**
   * Persist the current history for `workflowId`. Throttled internally
   * via lastPersistAtRef so a rapid mutation burst doesn't write every
   * frame. Trimmed to the last PERSIST_CAP entries to stay under
   * localStorage quotas.
   */
  const lastPersistAtRef = useRef(0);
  const PERSIST_THROTTLE_MS = 2000;
  const persistHistoryFor = useCallback((workflowId) => {
    if (!workflowId) return;
    const now = Date.now();
    if (now - lastPersistAtRef.current < PERSIST_THROTTLE_MS) return;
    lastPersistAtRef.current = now;
    try {
      const past = pastRef.current.slice(-PERSIST_CAP);
      const future = futureRef.current.slice(-PERSIST_CAP);
      const blob = JSON.stringify({ past, future, persistedAt: now });
      window.localStorage.setItem(STORAGE_PREFIX + workflowId, blob);
    } catch {
      // Quota exceeded / disabled → silently skip. The next reload
      // will simply start with empty undo history (pre-RC-118b behavior).
    }
  }, []);
  /* historyTick is read here ONLY to satisfy the lint rule and to anchor
   * the canUndo/canRedo flags' dependency on it visually — the values are
   * recomputed from refs but their reactivity comes from this tick. */
  // eslint-disable-next-line no-unused-expressions
  historyTick;

  // =========================================================================
  // RC-135 — Autosave (crash / tab-close / network-drop protection)
  // =========================================================================
  //
  // Companion to the undo persistence above: that stack lets the
  // operator REVERSE intentional edits across reloads; this one keeps
  // the LATEST snapshot so an unintentional reload (tab crash, OS
  // sleep killing the page, accidental close) doesn't lose unsaved
  // work. Together they cover both the "I want to undo what I did"
  // and "I lost work I never got to save" failure modes.
  //
  // Storage shape (one key per workflow):
  //   {
  //     nodes, connections,    // the snapshot itself
  //     baseVersion,            // the workflow version this snapshot
  //                             // was built ON TOP of — lets the
  //                             // restore prompt detect "someone else
  //                             // saved between my edit and my reload"
  //                             // and warn before clobbering their work
  //     savedAt,                // operator-facing "edits from {ago}"
  //   }
  //
  // Trade-offs:
  //   * 30s cadence is the operator-side throttle; tighter would burn
  //     localStorage write cycles for no real protection win, looser
  //     would risk losing up to a minute of edits on crash.
  //   * One snapshot per workflow (not a stack) — this is the safety
  //     net, not a history. The undo stack above is the history.
  //   * baseVersion check on restore: if the operator was editing v5
  //     and the server is now on v7, restoring would silently
  //     overwrite v6+v7. We surface that explicitly in the banner.
  //   * Soft-fail on every storage path — private-browsing / quota
  //     exceeded degrades to pre-RC-135 in-memory-only behavior.
  const UNSAVED_PREFIX = 'agentlets.canvas.unsaved.';

  /**
   * Write the current snapshot to the autosave slot for `workflowId`.
   * Caller passes `baseVersion` (the loaded workflow's version at this
   * moment) so the restore prompt can detect concurrent edits during
   * the disconnect. Caller is responsible for the 30s cadence;
   * canvas-side throttling lives in the consumer effect rather than
   * here so the helper stays a thin storage primitive.
   */
  const persistUnsavedFor = useCallback((workflowId, baseVersion) => {
    if (!workflowId) return;
    try {
      const blob = JSON.stringify({
        nodes: nodes,
        connections: connections,
        baseVersion: baseVersion == null ? null : Number(baseVersion),
        savedAt: Date.now(),
      });
      window.localStorage.setItem(UNSAVED_PREFIX + workflowId, blob);
    } catch {
      // Quota / disabled / corrupt → silent degrade. The operator's
      // next save still goes through; only the crash-recovery
      // affordance is lost.
    }
  }, [nodes, connections]);

  /**
   * Read the autosave slot for `workflowId`. Returns null when the
   * slot is empty, the stored blob is malformed, or localStorage is
   * unavailable. Pure read — does not mutate state and is safe to
   * call from a useEffect during load.
   */
  const readUnsavedFor = useCallback((workflowId) => {
    if (!workflowId) return null;
    try {
      const raw = window.localStorage.getItem(UNSAVED_PREFIX + workflowId);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      if (!Array.isArray(parsed.nodes) || !Array.isArray(parsed.connections)) return null;
      return {
        nodes: parsed.nodes,
        connections: parsed.connections,
        baseVersion: parsed.baseVersion == null ? null : Number(parsed.baseVersion),
        savedAt: parsed.savedAt == null ? null : Number(parsed.savedAt),
      };
    } catch {
      return null;
    }
  }, []);

  /**
   * Remove the autosave slot for `workflowId`. Called on save-success
   * AND on explicit Discard from the restore prompt, so a successful
   * save by the operator doesn't keep prompting them to restore a
   * snapshot they've already persisted upstream.
   */
  const clearUnsavedFor = useCallback((workflowId) => {
    if (!workflowId) return;
    try { window.localStorage.removeItem(UNSAVED_PREFIX + workflowId); }
    catch { /* see persistUnsavedFor — soft-fail is the contract */ }
  }, []);

  // =========================================================================
  // Port Configuration by Node Type
  // =========================================================================

  const getNodePortConfig = useCallback((type) => {
    // First try to get actual port definitions from soarNodes
    const soarPorts = getSoarNodePorts(type);
    if (soarPorts && (soarPorts.in.length > 0 || soarPorts.out.length > 0)) {
      return {
        in: soarPorts.in.map((p) => (typeof p === 'string' ? p : p.name || 'default')),
        out: soarPorts.out.map((p) => (typeof p === 'string' ? p : p.name || 'default')),
      };
    }

    // Fallback for generic types
    const portConfigs = {
      trigger: { in: [], out: ['default'] },
      action: { in: ['default'], out: ['default'] },
      condition: { in: ['default'], out: ['true', 'false'] },
      switch: { in: ['default'], out: ['case1', 'case2', 'case3', 'default'] },
      fork: { in: ['default'], out: ['branch1', 'branch2', 'branch3'] },
      join: { in: ['branch1', 'branch2', 'branch3'], out: ['default'] },
      approval: { in: ['default'], out: ['approved', 'denied'] },
      error_handler: { in: ['default'], out: ['success', 'error'] },
      terminal: { in: ['default'], out: [] },
    };
    return portConfigs[type] || { in: ['default'], out: ['default'] };
  }, []);

  // =========================================================================
  // Node Port Position Helpers
  // =========================================================================

  // ── Shape-aware port positioning ──
  // Uses the centralized SHAPE_GEOM to place ports ON the visual shape surface.
  // Diamond ports sit at the actual diamond vertices (which extend beyond NODE_H).
  // Bookend ports sit on the pill surface. Rect ports sit on the bounding box edges.

  const getNodePorts = useCallback((nodeId) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return [];

    const nx = Number.isFinite(node.x) ? node.x : 0;
    const ny = Number.isFinite(node.y) ? node.y : 0;
    const shape = getNodeShapeType(node.type);
    const geom = SHAPE_GEOM[shape];

    const config = getNodePortConfig(node.type);
    const ports = [];
    const cx = geom.centerX(nx);
    const topY = geom.topY(ny);
    const bottomY = geom.bottomY(ny);

    // ── Conductor OSS fan-out / fan-in pattern ──
    // ALL input ports collapse to a single top-center anchor.
    // ALL output ports collapse to a single bottom-center anchor.
    // Edges visually fan out / in via the edge routing layer (see CanvasConnection),
    // so there are no orphan port dots scattered across the node perimeter.
    config.in.forEach((portName) => {
      ports.push({ portName, side: 'top', x: cx, y: topY });
    });
    config.out.forEach((portName) => {
      ports.push({ portName, side: 'bottom', x: cx, y: bottomY });
    });

    return ports;
  }, [nodes, getNodePortConfig]);

  const getPortPosition = useCallback((nodeId, portName, side) => {
    const ports = getNodePorts(nodeId);
    // 1. Try exact match
    let port = ports.find((p) => p.portName === portName && p.side === side);
    // 2. Fallback: any port on the requested side (handles 'output'→'default', 'input'→'default' mismatches)
    if (!port) {
      if (process.env.NODE_ENV !== 'production' && portName && portName !== 'default') {
        console.warn(`[getPortPosition] Port mismatch: node=${nodeId}, port="${portName}", side="${side}" — falling back to first ${side} port`);
      }
      port = ports.find((p) => p.side === side);
    }
    // 3. Last resort: compute default position from node directly
    if (!port) {
      const node = nodes.find((n) => n.id === nodeId);
      if (node) {
        const nx = Number.isFinite(node.x) ? node.x : 0;
        const ny = Number.isFinite(node.y) ? node.y : 0;
        return side === 'bottom'
          ? { x: nx + NODE_W / 2, y: ny + NODE_H }
          : { x: nx + NODE_W / 2, y: ny };
      }
    }
    return port ? { x: port.x, y: port.y } : { x: 0, y: 0 };
  }, [getNodePorts, nodes]);

  // =========================================================================
  // DAG Validation (Cycle Detection + Node Connectivity)
  // =========================================================================

  const validateDAG = useCallback(() => {
    const errors = [];
    const warnings = [];

    // Build adjacency list for cycle detection
    const adj = new Map();
    nodes.forEach((n) => {
      if (!adj.has(n.id)) adj.set(n.id, []);
    });

    connections.forEach((c) => {
      if (adj.has(c.from)) adj.get(c.from).push(c.to);
    });

    // DFS-based cycle detection with coloring (white=0, gray=1, black=2)
    const color = new Map();
    const recStack = new Map();
    let hasCycle = false;

    const dfs = (nodeId) => {
      color.set(nodeId, 1); // gray
      recStack.set(nodeId, true);

      const neighbors = adj.get(nodeId) || [];
      for (const neighbor of neighbors) {
        if (!color.has(neighbor)) {
          dfs(neighbor);
        } else if (color.get(neighbor) === 1 && recStack.get(neighbor)) {
          // Back edge found
          hasCycle = true;
          errors.push(`Cycle detected: ${nodeId} -> ${neighbor}`);
        }
      }

      color.set(nodeId, 2); // black
      recStack.set(nodeId, false);
    };

    nodes.forEach((n) => {
      if (!color.has(n.id)) dfs(n.id);
    });

    // Terminal node types — these have no output ports and legitimately end workflow branches
    const TERMINAL_TYPES = new Set(['terminal', 'end', 'terminate']);

    // Validate node connectivity
    nodes.forEach((node) => {
      const isTrigger = TRIGGER_TYPES.has(node.type);
      const isTerminal = TERMINAL_TYPES.has(node.type);

      // Trigger nodes must have NO incoming connections
      if (isTrigger) {
        const hasIncoming = connections.some((c) => c.to === node.id);
        if (hasIncoming) {
          errors.push(`Trigger node ${node.label} must not have incoming connections`);
        }
      }

      // Non-trigger nodes should have at least one input
      if (!isTrigger) {
        const hasInput = connections.some((c) => c.to === node.id);
        if (!hasInput) {
          warnings.push(`Node ${node.label} has no input connections — it may be unreachable`);
        }
      }

      // Non-terminal nodes should have at least one output
      if (!isTerminal) {
        const hasOutput = connections.some((c) => c.from === node.id);
        if (!hasOutput) {
          warnings.push(`Node ${node.label} has no output connections`);
        }
      }

      // Validate required config by type
      if (node.type === 'condition' && (!node.config?.expression || node.config.expression.trim() === '')) {
        errors.push(`Condition node ${node.label} missing expression`);
      }
    });

    // Compute topological order (Khan's algorithm)
    const indegree = new Map();
    nodes.forEach((n) => {
      indegree.set(n.id, 0);
    });
    connections.forEach((c) => {
      indegree.set(c.to, (indegree.get(c.to) || 0) + 1);
    });

    const queue = Array.from(nodes)
      .filter((n) => indegree.get(n.id) === 0)
      .map((n) => n.id);
    const topologicalOrder = [];

    while (queue.length > 0) {
      const nodeId = queue.shift();
      topologicalOrder.push(nodeId);

      const neighbors = adj.get(nodeId) || [];
      neighbors.forEach((neighbor) => {
        indegree.set(neighbor, indegree.get(neighbor) - 1);
        if (indegree.get(neighbor) === 0) {
          queue.push(neighbor);
        }
      });
    }

    // Detect spaghetti connections — long connections to end node that should use terminate
    const depthMap = new Map();
    topologicalOrder.forEach((nodeId) => {
      let maxD = 0;
      connections.forEach((c) => {
        if (c.to === nodeId && depthMap.has(c.from)) {
          maxD = Math.max(maxD, depthMap.get(c.from) + 1);
        }
      });
      depthMap.set(nodeId, maxD);
    });

    connections.forEach((c) => {
      const toNode = nodes.find((n) => n.id === c.to);
      if (!toNode || toNode.type !== 'end') return;
      const fromDepth = depthMap.get(c.from);
      const toDepth = depthMap.get(c.to);
      if (fromDepth != null && toDepth != null && (toDepth - fromDepth) > 3) {
        const fromNode = nodes.find((n) => n.id === c.from);
        warnings.push(`Long connection from "${fromNode?.label || c.from}" to End spans ${toDepth - fromDepth} layers — consider using a Terminate node instead`);
      }
    });

    const valid = errors.length === 0 && !hasCycle;

    const validation = { valid, errors, warnings, topologicalOrder };
    setDagValidation(validation);
    return validation;
  }, [nodes, connections]);

  // =========================================================================
  // Topological Sort
  // =========================================================================

  const topologicalSort = useCallback(() => {
    const adj = new Map();
    nodes.forEach((n) => {
      if (!adj.has(n.id)) adj.set(n.id, []);
    });

    connections.forEach((c) => {
      if (adj.has(c.from)) adj.get(c.from).push(c.to);
    });

    const indegree = new Map();
    nodes.forEach((n) => {
      indegree.set(n.id, 0);
    });

    connections.forEach((c) => {
      indegree.set(c.to, (indegree.get(c.to) || 0) + 1);
    });

    const queue = Array.from(nodes)
      .filter((n) => indegree.get(n.id) === 0)
      .map((n) => n.id);
    const result = [];

    while (queue.length > 0) {
      const nodeId = queue.shift();
      result.push(nodeId);

      const neighbors = adj.get(nodeId) || [];
      neighbors.forEach((neighbor) => {
        indegree.set(neighbor, indegree.get(neighbor) - 1);
        if (indegree.get(neighbor) === 0) {
          queue.push(neighbor);
        }
      });
    }

    return result;
  }, [nodes, connections]);

  // =========================================================================
  // Execution Plan (Group nodes into parallel-executable steps)
  // =========================================================================

  const getExecutionPlan = useCallback(() => {
    const topoOrder = topologicalSort();
    if (topoOrder.length === 0) return [];

    const inDegree = new Map();
    const outEdges = new Map();

    nodes.forEach((n) => {
      inDegree.set(n.id, 0);
      outEdges.set(n.id, []);
    });

    connections.forEach((c) => {
      inDegree.set(c.to, (inDegree.get(c.to) || 0) + 1);
      outEdges.get(c.from).push(c.to);
    });

    const plan = [];
    const processed = new Set();

    topoOrder.forEach((nodeId) => {
      // Check if all predecessors are processed
      let canExecute = true;
      connections.forEach((c) => {
        if (c.to === nodeId && !processed.has(c.from)) {
          canExecute = false;
        }
      });

      if (canExecute) {
        // Find which step this node belongs to
        let step = 0;
        connections.forEach((c) => {
          if (c.to === nodeId && processed.has(c.from)) {
            const predStep = plan.findIndex((s) => s.includes(c.from));
            step = Math.max(step, predStep + 1);
          }
        });

        if (!plan[step]) plan[step] = [];
        plan[step].push(nodeId);
        processed.add(nodeId);
      }
    });

    return plan.filter((s) => s.length > 0);
  }, [nodes, connections, topologicalSort]);

  // =========================================================================
  // Connection Validation
  // =========================================================================

  const canConnect = useCallback(
    (fromId, fromPort, toId, toPort) => {
      // No self-connections
      if (fromId === toId) return false;

      // No duplicate connections on same ports. Routed through the shared
      // edgeKey so the editor and the loader cannot disagree about what a
      // duplicate is — they did, and the loader won.
      const candidateKey = edgeKey({ from: fromId, fromPort, to: toId, toPort });
      if (connections.some((c) => edgeKey(c) === candidateKey)) {
        return false;
      }

      // No connections INTO trigger nodes (triggers are always roots with zero incoming)
      const targetNode = nodes.find((n) => n.id === toId);
      if (targetNode && TRIGGER_TYPES.has(targetNode.type)) return false;

      // Check if connection would create a cycle
      // (Simple check: verify toId doesn't have a path to fromId)
      const visited = new Set();
      const hasCyclePath = (nodeId) => {
        if (nodeId === fromId) return true;
        if (visited.has(nodeId)) return false;
        visited.add(nodeId);
        const outgoing = connections.filter((c) => c.from === nodeId);
        return outgoing.some((c) => hasCyclePath(c.to));
      };

      if (hasCyclePath(toId)) return false;

      return true;
    },
    [connections, nodes]
  );

  // =========================================================================
  // Execution State Management
  // =========================================================================

  const setNodeExecutionState = useCallback((nodeId, state) => {
    setExecutionState((prev) => new Map(prev).set(nodeId, state));
  }, []);

  const setExecutionStates = useCallback((stateMap) => {
    setExecutionState((prev) => {
      const updated = new Map(prev);
      Object.entries(stateMap).forEach(([nodeId, state]) => {
        updated.set(nodeId, state);
      });
      return updated;
    });
  }, []);

  const clearExecutionState = useCallback(() => {
    setExecutionState(new Map());
  }, []);

  const getRunningNodes = useCallback(() => {
    return Array.from(executionState.entries())
      .filter(([_, state]) => state.status === 'running' || state.status === 'pending')
      .map(([nodeId, _]) => nodeId);
  }, [executionState]);

  const getExecutionProgress = useCallback(() => {
    const states = Array.from(executionState.values());
    return {
      total: states.length,
      completed: states.filter((s) => s.status === 'completed').length,
      failed: states.filter((s) => s.status === 'failed').length,
      running: states.filter((s) => s.status === 'running').length,
      pending: states.filter((s) => s.status === 'pending').length,
    };
  }, [executionState]);

  // =========================================================================
  // Multi-Select Operations
  // =========================================================================

  const toggleMultiSelect = useCallback((nodeId) => {
    setMultiSelect((prev) => {
      const updated = new Set(prev);
      if (updated.has(nodeId)) {
        updated.delete(nodeId);
      } else {
        updated.add(nodeId);
      }
      return updated;
    });
    setSelectedType('multi');
  }, []);

  const selectRegion = useCallback((x1, y1, x2, y2) => {
    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);

    const selected = nodes.filter(
      (n) =>
        n.x >= minX &&
        n.x + NODE_W <= maxX &&
        n.y >= minY &&
        n.y + NODE_H <= maxY
    );

    setMultiSelect(new Set(selected.map((n) => n.id)));
    setSelectedType('multi');
  }, [nodes]);

  const moveSelected = useCallback((dx, dy) => {
    setNodes((prev) =>
      prev.map((n) =>
        multiSelect.has(n.id) ? { ...n, x: n.x + dx, y: n.y + dy } : n
      )
    );
    markDirty();
  }, [multiSelect, markDirty]);

  const deleteSelectedNodes = useCallback(() => {
    const toDelete = Array.from(multiSelect);
    setNodes((prev) => prev.filter((n) => !toDelete.includes(n.id)));
    setConnections((prev) =>
      prev.filter((c) => !toDelete.includes(c.from) && !toDelete.includes(c.to))
    );
    setMultiSelect(new Set());
    setSelectedType(null);
    markDirty();
  }, [multiSelect, markDirty]);

  // =========================================================================
  // Snap to Grid
  // =========================================================================

  const snapToGrid = useCallback((x, y) => ({
    x: Math.round(x / GRID_SIZE) * GRID_SIZE,
    y: Math.round(y / GRID_SIZE) * GRID_SIZE,
  }), []);

  // =========================================================================
  // Auto-Layout
  // =========================================================================

  const autoLayout = useCallback(() => {
    // First, validate and repair the DAG to ensure no cycles or orphans
    const dagResult = validateAndRepairDAG(nodes, connections);
    if (dagResult.repairs.length > 0) {
      console.warn('[autoLayout] DAG repairs applied:', dagResult.repairs);
      setConnections(dagResult.connections);
    }

    if (dagResult.nodes.length === 0) return;

    // Clear every manualPosition pin — the explicit "Auto-layout" action is
    // the documented reset path for the pin flag (see CANVAS_PRODUCTION_AUDIT
    // P0-A). Mutating dagResult.nodes here is safe; it's a fresh array
    // returned by validateAndRepairDAG and not retained elsewhere.
    for (let i = 0; i < dagResult.nodes.length; i++) {
      const n = dagResult.nodes[i];
      if (n && n.manualPosition === true) {
        dagResult.nodes[i] = { ...n, manualPosition: false };
      }
    }

    // Adaptive spacing based on workflow complexity — tighter for dense DAGs
    // so the whole graph still fits on-screen at zoomToFit.
    const totalNodes = dagResult.nodes.length;
    const rankSep = totalNodes <= 8 ? 140 : totalNodes <= 20 ? 120 : 100;
    const nodeSep = totalNodes <= 8 ? 80 : totalNodes <= 20 ? 60 : 40;

    // Always strip stale bend points — regardless of which engine we use next
    setConnections((prev) =>
      prev.map((c) => {
        if (c.bendPoints === undefined && c.labelPosition === undefined) return c;
        const { bendPoints: _bp, labelPosition: _lp, ...rest } = c;
        return rest;
      })
    );

    const useElk = (typeof window !== 'undefined')
      && window.localStorage?.getItem('canvas.useElk') === 'true';

    if (useElk) {
      // ELK async path — opt-in via localStorage until FORK/JOIN partitioning lands
      elkLayoutDAG(dagResult.nodes, dagResult.connections, {
        nodeWidth: NODE_W, nodeHeight: NODE_H, rankSep, nodeSep, padding: 80, rankDir: 'TB',
      }).then(({ nodes: positioned, edges: routedEdges }) => {
        const posMap = new Map(positioned.map((n) => [n.id, { x: n.x, y: n.y }]));
        setNodes((prev) => prev.map((n) => {
          const pos = posMap.get(n.id);
          return pos ? { ...n, x: pos.x, y: pos.y } : n;
        }));
        const bendMap = new Map(routedEdges.map((e) => [`${e.from}__${e.to}`, e]));
        setConnections((prev) => prev.map((c) => {
          const routed = bendMap.get(`${c.from}__${c.to}`);
          return routed ? { ...c, bendPoints: routed.bendPoints, labelPosition: routed.labelPosition } : c;
        }));
      }).catch((err) => {
        console.error('[autoLayout] ELK failed, falling back to Sugiyama', err);
        try {
          const positioned = runLayout(dagResult.nodes, dagResult.connections, { nodeWidth: NODE_W, nodeHeight: NODE_H, rankSep, nodeSep, padding: 80, rankDir: 'TB' }, irLaidRef);
          const posMap = new Map(positioned.map((n) => [n.id, { x: n.x, y: n.y }]));
          setNodes((prev) => prev.map((n) => {
            const pos = posMap.get(n.id);
            return pos ? { ...n, x: pos.x, y: pos.y } : n;
          }));
        } catch (e2) { console.error('[autoLayout] Sugiyama also failed', e2); }
      });
    } else {
      // Default: synchronous Sugiyama (or IR pipeline when the flag is set).
      try {
        const positioned = runLayout(dagResult.nodes, dagResult.connections, {
          nodeWidth: NODE_W, nodeHeight: NODE_H, rankSep, nodeSep, padding: 80, rankDir: 'TB',
        }, irLaidRef);
        const posMap = new Map(positioned.map((n) => [n.id, { x: n.x, y: n.y }]));
        setNodes((prev) => prev.map((n) => {
          const pos = posMap.get(n.id);
          return pos ? { ...n, x: pos.x, y: pos.y } : n;
        }));
        // Long-edge bend points: optional feature — threading connections
        // through dummy positions can produce zigzags on short spans. Gate
        // behind a localStorage flag until trunk anchoring (Phase 1b) makes
        // straight diagonals look clean without help.
        const useLongEdgeBends = (typeof window !== 'undefined')
          && window.localStorage?.getItem('canvas.longEdgeBends') === 'true';
        const bendPointsMap = positioned.__bendPoints;
        if (useLongEdgeBends && bendPointsMap && bendPointsMap.size > 0) {
          setConnections((prev) => prev.map((c) => {
            const bps = bendPointsMap.get(`${c.from}__${c.to}`);
            return bps ? { ...c, bendPoints: bps } : c;
          }));
        }
      } catch (err) {
        console.error('[autoLayout] Sugiyama layout failed', err);
      }
    }

    markDirty();
  }, [nodes, connections, markDirty]);

  // =========================================================================
  // Data Flow Management
  // =========================================================================

  const updateConnectionDataFlow = useCallback((connId, { sourceVar, targetInput }) => {
    setDataFlow((prev) => {
      const updated = new Map(prev);
      updated.set(connId, { sourceVar, targetInput, dataPreview: null });
      return updated;
    });
    markDirty();
  }, [markDirty]);

  const getNodeInputData = useCallback((nodeId) => {
    const inputData = {};
    connections.forEach((conn) => {
      if (conn.to === nodeId) {
        const flow = dataFlow.get(conn.id);
        const key = flow?.targetInput || conn.toPort || 'default';
        inputData[key] = {
          fromNode: conn.from,
          fromPort: conn.fromPort,
          sourceVar: flow?.sourceVar,
        };
      }
    });
    return inputData;
  }, [connections, dataFlow]);

  const getNodeOutputSchema = useCallback((nodeId) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return {};

    // Output schema depends on node type
    const schemas = {
      trigger: { timestamp: 'date', payload: 'object' },
      action: { result: 'any', output: 'any' },
      condition: { result: 'boolean' },
      approval: { approved: 'boolean', approver: 'string' },
      error_handler: { error: 'object', handled: 'boolean' },
    };

    return schemas[node.type] || { output: 'any' };
  }, [nodes]);

  // =========================================================================
  // Node Operations
  // =========================================================================

  /**
   * Pure helper: run Sugiyama layout over (nodes, connections) and return
   * nodes with updated x/y. Never mutates state; callers setNodes with the
   * return value. Used by addNode/insertNodeOnConnection to re-tidy the
   * canvas immediately on insert (prevents "stacked up" drops).
   */
  /**
   * Auto-layout the canvas while honoring user-pinned nodes.
   *
   * A node is "pinned" when `manualPosition === true` — set every time the
   * user drags it (see drag/endDrag below) and persisted in the workflow
   * JSON. Pinned nodes keep their saved (x, y); the layout engine still sees
   * them so the unpinned nodes route around them, but their final coordinates
   * are NOT overwritten.
   *
   * Why this exists: prior to the pin flag, every state change (add node,
   * splice on edge, undo, etc.) re-ran the layout and snapped manually-moved
   * nodes back to their algorithmic positions, making node movement feel
   * broken from the user's perspective.
   *
   * Reset path: the toolbar "Auto-layout" action (`autoLayout()`) clears the
   * `manualPosition` flag on every node and re-runs layout from scratch.
   */
  const computeLayoutPositions = useCallback((targetNodes, targetConnections) => {
    if (!targetNodes || targetNodes.length === 0) return targetNodes;
    const totalNodes = targetNodes.length;
    const rankSep = totalNodes <= 8 ? 140 : totalNodes <= 20 ? 120 : 100;
    const nodeSep = totalNodes <= 8 ? 80 : totalNodes <= 20 ? 60 : 40;
    try {
      const positioned = runLayout(targetNodes, targetConnections, {
        nodeWidth: NODE_W, nodeHeight: NODE_H, rankSep, nodeSep, padding: 80, rankDir: 'TB',
      }, irLaidRef);
      const posMap = new Map(positioned.map((n) => [n.id, { x: n.x, y: n.y }]));
      return targetNodes.map((n) => {
        // Pinned by user: preserve their coordinates verbatim.
        if (n && n.manualPosition === true && Number.isFinite(n.x) && Number.isFinite(n.y)) {
          return n;
        }
        const pos = posMap.get(n.id);
        return pos ? { ...n, x: pos.x, y: pos.y } : n;
      });
    } catch (err) {
      console.error('[computeLayoutPositions] layout failed', err);
      return targetNodes;
    }
  }, []);

  /**
   * addNode places a brand-new, unconnected node on the canvas.
   *
   * The node is created with `manualPosition: true` so it stays exactly where
   * the caller asked for it — auto-layout will treat it as a user-pinned node.
   * This is the right behaviour for palette drops onto empty canvas: the user
   * pointed at coordinates, those coordinates win. To slot a new node INTO an
   * existing edge (auto-layout participating), use `insertNodeOnConnection`
   * instead.
   *
   * @param {string} type     node type id
   * @param {number} x        drop x in canvas coords
   * @param {number} y        drop y in canvas coords
   * @param {object} config   initial node config
   * @param {string} label    display label
   * @returns {string} new node id
   */
  const addNode = useCallback((type, x, y, config = {}, label = '') => {
    const id = `n_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`;
    // Guard against NaN/Infinity positions (can happen if zoom=0 or pan is undefined)
    const safeX = Number.isFinite(x) ? x : 200;
    const safeY = Number.isFinite(y) ? y : 200;
    const newNode = {
      id,
      type,
      x: safeX,
      y: safeY,
      config,
      label: label || type,
      manualPosition: true,
    };
    // Append without re-running layout — the new node is unconnected to the
    // existing DAG, so layout would only re-shuffle the rest of the graph
    // pointlessly and (worse) move the new node away from the drop point.
    setNodes((prev) => [...prev, newNode]);
    markDirty();
    return id;
  }, [markDirty]);

  const updateNode = useCallback((nodeId, updates) => {
    // Shallow-merge preserves any top-level field the caller didn't
    // touch (label, position, type, ...). Callers that update
    // `config` MUST spread the existing config — `{ config: {
    // ...node.config, [field]: value } }` — otherwise unrendered
    // backend-added fields (e.g. `outputMapping`, `cases`,
    // `formSchema`, `sourceTaskRef`, anything the inspector doesn't
    // explicitly render) would be dropped at save time. Every
    // call-site in the codebase follows this pattern; adding a dev-
    // mode warning catches regressions before they ship.
    if (process.env.NODE_ENV !== 'production'
            && updates && Object.prototype.hasOwnProperty.call(updates, 'config')
            && updates.config && typeof updates.config === 'object') {
      const prevNode = nodes.find((x) => x && x.id === nodeId);
      const prevCfg = prevNode?.config || {};
      const dropped = Object.keys(prevCfg).filter((k) => !(k in updates.config));
      if (dropped.length > 0) {
        // eslint-disable-next-line no-console
        console.warn('[useCanvas.updateNode] Caller replaced config without spreading; '
                + 'these existing fields would be DROPPED:', dropped,
                '\nFix: { config: { ...node.config, [field]: value } }');
      }
    }
    setNodes((prev) => prev.map((n) => (n.id === nodeId ? { ...n, ...updates } : n)));
    markDirty();
  }, [markDirty, nodes]);

  // DEL-RESTITCH (V4 Phase 6 bugfix) — when a mid-spine node is deleted, the
  // downstream chain MUST be re-stitched to the node's parent(s). Without this:
  //   • V3 leaves the downstream nodes visible-but-orphaned, forcing the user
  //     to manually re-wire (annoying but not data-loss).
  //   • V4's IR pipeline (flatToIR + ELK) silently DROPS unreachable-from-
  //     Start nodes from the rendered DAG (see useCanvas.js:1318-1334). To
  //     the user it looks like the entire downstream chain vanished. The
  //     nodes are still in `canvas.nodes` state — they just don't render.
  //
  // The cartesian-product behavior (every incoming × every outgoing) matches
  // user intuition for both common cases:
  //   • Linear task on the spine: 1 in × 1 out = 1 bypass edge → seamless splice.
  //   • Fork being deleted: 1 in × N out = N bypass edges to each downstream
  //     branch head, preserving fan-out.
  //   • Join being deleted: N in × 1 out = N upstream branches all feed the
  //     downstream node directly.
  // We dedupe (skip new edges that already exist) to avoid the validateAndRepairDAG
  // duplicate-edge removal Phase 3b cleanup.
  const deleteNode = useCallback((nodeId) => {
    setConnections((prev) => {
      const incoming = prev.filter((c) => c.to === nodeId);
      const outgoing = prev.filter((c) => c.from === nodeId);
      const survivors = prev.filter((c) => c.from !== nodeId && c.to !== nodeId);
      const existingKeys = new Set(survivors.map((c) => `${c.from}→${c.to}`));
      const bypass = [];
      for (const inEdge of incoming) {
        for (const outEdge of outgoing) {
          const key = `${inEdge.from}→${outEdge.to}`;
          if (existingKeys.has(key)) continue;       // dedupe vs survivors
          if (inEdge.from === outEdge.to) continue;  // would be a self-loop
          existingKeys.add(key);
          bypass.push({
            id: `c_restitch_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            from: inEdge.from,
            fromPort: inEdge.fromPort || 'default',
            to: outEdge.to,
            toPort: outEdge.toPort || 'default',
          });
        }
      }
      return [...survivors, ...bypass];
    });
    setNodes((prev) => prev.filter((n) => n.id !== nodeId));
    setExecutionState((prev) => {
      const updated = new Map(prev);
      updated.delete(nodeId);
      return updated;
    });
    if (selected === nodeId) {
      setSelected(null);
      setSelectedType(null);
    }
    markDirty();
  }, [selected, markDirty]);

  // =========================================================================
  // Connection Operations
  // =========================================================================

  const addConnection = useCallback(
    (fromId, fromPort, toId, toPort) => {
      if (!canConnect(fromId, fromPort, toId, toPort)) return null;

      const id = `c_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`;
      const conn = {
        id,
        from: fromId,
        fromPort: fromPort || 'default',
        to: toId,
        toPort: toPort || 'default',
      };
      setConnections((prev) => [...prev, conn]);
      markDirty();
      return id;
    },
    [canConnect, markDirty]
  );

  const deleteConnection = useCallback((connId) => {
    setConnections((prev) => prev.filter((c) => c.id !== connId));
    setDataFlow((prev) => {
      const updated = new Map(prev);
      updated.delete(connId);
      return updated;
    });
    if (selected === connId) {
      setSelected(null);
      setSelectedType(null);
    }
    markDirty();
  }, [selected, markDirty]);

  // =========================================================================
  // Sequential Insertion — Conductor OSS-style "+" button between nodes
  // =========================================================================

  /**
   * Insert a new node on an existing connection, splitting it into two connections.
   * @param {string} connectionId - The connection to split
   * @param {string} nodeType - Type of node to insert (e.g. 'simple', 'http')
   * @param {string} label - Label for the new node
   * @param {object} config - Optional config for the new node
   * @returns {string} The new node's ID
   */
  const insertNodeOnConnection = useCallback((connectionId, nodeType, label = '', config = {}) => {
    const conn = connections.find(c => c.id === connectionId);
    if (!conn) return null;

    const fromNode = nodes.find(n => n.id === conn.from);
    const toNode = nodes.find(n => n.id === conn.to);
    if (!fromNode || !toNode) return null;

    // Place new node at midpoint between source and target (pre-layout).
    const midX = (fromNode.x + toNode.x) / 2;
    const midY = (fromNode.y + toNode.y) / 2;
    const newNodeId = `n_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`;
    const newNode = {
      id: newNodeId,
      type: nodeType,
      x: Number.isFinite(midX) ? midX : 200,
      y: Number.isFinite(midY) ? midY : 200,
      config,
      label: label || nodeType,
    };

    // Build the two replacement connections: source→newNode, newNode→target
    const ts = Date.now();
    const conn1 = {
      id: `c_${ts}_${Math.random().toString(36).substr(2, 4)}`,
      from: conn.from,
      fromPort: conn.fromPort || 'default',
      to: newNodeId,
      toPort: 'default',
    };
    const conn2 = {
      id: `c_${ts + 1}_${Math.random().toString(36).substr(2, 4)}`,
      from: newNodeId,
      fromPort: 'default',
      to: conn.to,
      toPort: conn.toPort || 'default',
    };

    // Compute the next full state atomically, then auto-layout the result so
    // the inserted node and its new edges slot cleanly into the DAG instead
    // of stacking on top of the midpoint.
    const nextConnections = [
      ...connections.filter(c => c.id !== connectionId),
      conn1,
      conn2,
    ];
    const nextNodesRaw = [...nodes, newNode];
    const nextNodesPositioned = computeLayoutPositions(nextNodesRaw, nextConnections);

    setNodes(nextNodesPositioned);
    setConnections(nextConnections);

    // Select the new node so user can configure it
    setSelected(newNodeId);
    setSelectedType('node');

    markDirty();
    return newNodeId;
  }, [connections, nodes, markDirty, computeLayoutPositions]);

  // =========================================================================
  // Append-after — terminal-leaf "+" button (Phase 3)
  // =========================================================================

  /**
   * Append a brand-new node after `parentId`, wiring a single edge
   * `parent → newNode`. Used by the terminal-leaf "+" button (a node with no
   * outgoing edges) and by the empty-canvas "+" below the START node.
   *
   * Differs from `insertNodeOnConnection` in two ways:
   *   • The parent has no existing outgoing edge to splice — the new node's
   *     incoming edge is brand new.
   *   • Auto-layout still runs so the new node lands cleanly under the parent.
   *
   * Selects the new node so the user can immediately configure it. Returns
   * the new node id.
   *
   * @param {string} parentId
   * @param {string} type
   * @param {string} [label]
   * @param {object} [config]
   * @returns {string|null}  id of the new node, or null if parent missing
   */
  const appendNodeAfter = useCallback((parentId, type, label = '', config = {}) => {
    const parent = nodes.find((n) => n.id === parentId);
    if (!parent) return null;

    // Tentative position immediately below the parent. Auto-layout will move
    // it to its final slot once we feed the new graph through Sugiyama / ELK.
    const newNodeId = `n_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`;
    const newNode = {
      id: newNodeId,
      type,
      x: Number.isFinite(parent.x) ? parent.x : 200,
      y: Number.isFinite(parent.y) ? parent.y + NODE_H + V_GAP : 300,
      config,
      label: label || type,
    };

    const newEdge = {
      id: `c_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      from: parentId,
      fromPort: 'default',
      to: newNodeId,
      toPort: 'default',
    };

    const nextNodes = [...nodes, newNode];
    const nextConnections = [...connections, newEdge];
    const positioned = computeLayoutPositions(nextNodes, nextConnections);

    setNodes(positioned);
    setConnections(nextConnections);
    setSelected(newNodeId);
    setSelectedType('node');
    markDirty();
    return newNodeId;
  }, [nodes, connections, computeLayoutPositions, markDirty]);

  /**
   * Convenience over `insertNodeOnConnection` that defaults the inserted
   * type to `terminate`. Used by per-branch "+" buttons whose primary
   * common case is closing a switch branch.
   *
   * @param {string} branchEdgeId
   * @param {string} [type='terminate']
   * @param {string} [label]
   * @returns {string|null}
   */
  const appendBranchTerminator = useCallback((branchEdgeId, type = 'terminate', label = '') => {
    return insertNodeOnConnection(branchEdgeId, type, label || 'Terminate');
  }, [insertNodeOnConnection]);

  // =========================================================================
  // addTaskToFlow — single primitive for user-initiated task insertion
  // =========================================================================
  //
  // PROBLEM THIS SOLVES
  // ────────────────────
  // The IR pipeline (flatToIR + ELK) only emits nodes reachable from Start.
  // Orphan nodes — nodes with no edges connecting them to the main flow —
  // get silently dropped from the rendered DAG. They show up in the JSON
  // Code panel because canvas state has them, but the user cannot see them
  // on the canvas. This produced bug reports of the form "I drag a Human
  // Task onto the canvas, the JSON shows it was added, but the DAG still
  // looks the same."
  //
  // Historically every user-insertion code path (palette click, palette
  // drag-drop, ⌘E picker palette mode, +Task toolbar, free drop on empty
  // area) had to remember to call `insertNodeOnConnection` themselves and
  // implement their own splice-or-add logic. Each path had subtle
  // differences and at least one of them always missed the auto-wire,
  // producing orphans. Centralising the policy here is the only way to
  // keep the invariant ("every user-added task is reachable from Start")
  // across every entry point.
  //
  // DECISION TREE (in priority order)
  // ─────────────────────────────────
  //   1. anchor.kind === 'edge'         → splice on that edge
  //   2. anchor.kind === 'after-node'   → splice the parent's outgoing edge,
  //                                        else appendNodeAfter
  //   3. anchor.kind === 'branch'       → splice the branch edge
  //   4. anchor === null  + End sentinel exists with feeding edge
  //                                      → splice that edge ("append before End")
  //   5. anchor === null  + Start sentinel exists with no End yet
  //                                      → appendNodeAfter(Start)
  //   6. otherwise (truly empty canvas) → free addNode at fallbackXY
  //
  // RETURNS
  // ───────
  //   { id, mode: 'edge' | 'after-node' | 'append-before-end' | 'append-after-start' | 'free',
  //     toast: string }
  //
  // Callers are expected to surface `toast` via their own toast system —
  // this primitive doesn't reach into UI plumbing. `id` is null only when
  // the caller passed an unknown anchor.kind or a malformed graph (e.g.
  // an edge id that doesn't exist).
  //
  // CALLERS MUST USE THIS for any user-initiated task insertion. The
  // lower-level primitives (addNode / insertNodeOnConnection /
  // appendNodeAfter) remain available for programmatic flows like canvas
  // load, copy/paste, recipe application — places where the caller already
  // owns the wiring decision.
  const addTaskToFlow = useCallback((type, label = '', config = {}, anchor = null, fallbackXY = null) => {
    const safeLabel = label || type;
    const isEndType = (n) => n && (n.type === 'end' || n.type === 'terminate');
    const isStartType = (n) => n && (
      n.type === 'manual_start' || n.type === 'start' || n.type === 'trigger'
      || n.type === 'webhook' || n.type === 'webhook_trigger'
      || n.type === 'schedule' || n.type === 'schedule_trigger' || n.type === 'cron'
      || n.type === 'siem_alert' || n.type === 'alert_trigger'
      || n.type === 'email_trigger' || n.type === 'email'
      || n.type === 'system_event_trigger' || n.type === 'system_event'
    );

    // RC-125 — Explicit-anchor branches (1, 2, 3 below) used to return
    // `{ id: null }` whenever the underlying primitive failed to resolve
    // the anchor (stale connectionId / branchEdgeId / nodeId because the
    // canvas state moved between picker-open and picker-confirm — e.g. an
    // auto-layout pass, an AI fix, a delete, or a load). The caller then
    // fired a "Could not insert task — anchor missing or invalid" toast
    // and the operator's work was dropped on the floor.
    //
    // Each explicit-anchor branch now attempts its primitive, returns
    // success if it worked, and otherwise FALLS THROUGH to the
    // no-anchor cascade (cases 4-6) below. The cascade lands the new
    // task in the most semantically reasonable spot (before End / after
    // Start / free), and its toast accurately reflects where it went so
    // the operator sees "Added <X> before End" instead of a useless
    // failure message. Same fix shape as RC-126 (orphan-defenses): when
    // the primary path fails because of state desync, gracefully fall
    // through instead of erroring.

    // 1. Explicit edge target
    if (anchor && anchor.kind === 'edge' && anchor.connectionId) {
      const id = insertNodeOnConnection(anchor.connectionId, type, safeLabel, config);
      if (id) {
        return { id, mode: 'edge', toast: `Inserted ${safeLabel} on edge` };
      }
      // RC-125 — edge gone; fall through to no-anchor cascade.
      // eslint-disable-next-line no-console
      console.warn('[addTaskToFlow/RC-125] edge anchor stale — falling through to cascade', { connectionId: anchor.connectionId });
    }

    // 3. Branch target (switch / fork branch edge — same splice semantics)
    if (anchor && anchor.kind === 'branch' && anchor.branchEdgeId) {
      const id = insertNodeOnConnection(anchor.branchEdgeId, type, safeLabel, config);
      if (id) {
        return { id, mode: 'edge', toast: `Inserted ${safeLabel} on branch` };
      }
      // RC-125 — branch edge gone; fall through.
      // eslint-disable-next-line no-console
      console.warn('[addTaskToFlow/RC-125] branch anchor stale — falling through to cascade', { branchEdgeId: anchor.branchEdgeId });
    }

    // 2. After-node target — splice outgoing edge if present, else append
    if (anchor && anchor.kind === 'after-node' && anchor.nodeId) {
      const parentExists = (nodes || []).some((n) => n && n.id === anchor.nodeId);
      if (parentExists) {
        const out = (connections || []).find((c) => c.from === anchor.nodeId);
        if (out) {
          const id = insertNodeOnConnection(out.id, type, safeLabel, config);
          if (id) {
            return { id, mode: 'after-node', toast: `Inserted ${safeLabel}` };
          }
          // Outgoing edge id moved between read + splice; try appendNodeAfter.
        }
        const id = appendNodeAfter(anchor.nodeId, type, safeLabel, config);
        if (id) {
          return { id, mode: 'after-node', toast: `Inserted ${safeLabel}` };
        }
      }
      // RC-125 — parent node gone or both primitives failed; fall through.
      // eslint-disable-next-line no-console
      console.warn('[addTaskToFlow/RC-125] after-node anchor stale — falling through to cascade', { nodeId: anchor.nodeId });
    }

    // 4. No anchor + End sentinel with a feeding edge → splice before End.
    //    This is the canonical "user dropped on empty canvas" recovery.
    const endNode = (nodes || []).find(isEndType);
    if (endNode) {
      const connToEnd = (connections || []).find((c) => c.to === endNode.id);
      if (connToEnd) {
        const id = insertNodeOnConnection(connToEnd.id, type, safeLabel, config);
        return { id, mode: 'append-before-end', toast: `Added ${safeLabel} before End` };
      }
    }

    // 5. No End yet, but a Start exists → wire after Start so the new
    //    node is reachable from the entry. This handles the half-built
    //    canvas case (user just placed a Start and is adding their first
    //    task before adding an End).
    const startNode = (nodes || []).find(isStartType);
    if (startNode) {
      const id = appendNodeAfter(startNode.id, type, safeLabel, config);
      return { id, mode: 'append-after-start', toast: `Added ${safeLabel} after Start` };
    }

    // 6. Truly empty canvas — free placement at the requested coords. The
    //    caller's coords win, no auto-layout (manualPosition: true). This
    //    matches the original addNode contract for the first-node case.
    const x = fallbackXY && Number.isFinite(fallbackXY.x) ? fallbackXY.x : 200;
    const y = fallbackXY && Number.isFinite(fallbackXY.y) ? fallbackXY.y : 200;
    const id = addNode(type, x, y, config, safeLabel);
    // Canvas Round 1 Patch C — mode 6 also fires when the canvas has
    // nodes but no Start AND no End sentinel (rare: imported legacy
    // graphs, partially-built workflows without proper boundaries). In
    // that case the new node lands disconnected — the audit's "node
    // sits there detached" symptom. Tell the user explicitly so they
    // can wire it via drag-to-connect (Round 1 Patch B restored that).
    // For the truly-empty case (first node ever), the original toast
    // is preserved since there's nothing to connect to.
    const hasOtherNodes = Array.isArray(nodes) && nodes.length > 0;
    const toast = hasOtherNodes
      ? `Added ${safeLabel} (disconnected — drag from another node to connect it)`
      : `Added ${safeLabel}`;
    return { id, mode: 'free', toast };
  }, [nodes, connections, insertNodeOnConnection, appendNodeAfter, addNode]);

  /**
   * Apply a curated multi-node recipe atomically.
   *
   * Splices the recipe DAG into the target edge so the new chain becomes:
   *   originalFrom  →  recipe.entry … recipe.exit  →  originalTo
   *
   * Internal recipe edges are wired verbatim (with port hints if the recipe
   * specifies `fromPort`). All recipe nodes get fresh runtime ids generated
   * here so re-applying the same recipe twice produces two independent
   * chains rather than colliding ids.
   *
   * Single auto-layout pass at the end (not per-node) keeps layout cost
   * bounded regardless of recipe size; pinned nodes are preserved per P0-A.
   *
   * @param {{ kind: 'edge', connectionId: string }} target
   * @param {object} recipe — see data/canvasRecipes.js for shape
   * @returns {string[]|null}  ids of the new nodes in recipe-declared order,
   *                           or null on a structural error.
   */
  const applyRecipe = useCallback((target, recipe) => {
    if (!target || target.kind !== 'edge') {
      console.warn('[applyRecipe] only edge targets are supported in v1');
      return null;
    }
    const conn = connections.find((c) => c && c.id === target.connectionId);
    if (!conn) {
      console.warn('[applyRecipe] target connection not found:', target.connectionId);
      return null;
    }
    const fromNode = nodes.find((n) => n.id === conn.from);
    const toNode = nodes.find((n) => n.id === conn.to);
    if (!fromNode || !toNode) {
      console.warn('[applyRecipe] target connection has dangling endpoints');
      return null;
    }
    if (!recipe || !Array.isArray(recipe.nodes) || recipe.nodes.length === 0) {
      console.warn('[applyRecipe] empty recipe');
      return null;
    }

    // 1. Generate fresh runtime ids per recipe node.
    const ts = Date.now();
    const idMap = new Map();
    const newNodes = recipe.nodes.map((rn, i) => {
      const newId = `n_${ts + i}_${Math.random().toString(36).substr(2, 4)}`;
      idMap.set(rn.id, newId);
      // Tentative coords on the line between fromNode and toNode; auto-
      // layout will move them to their final slots in step 4.
      const fr = i / Math.max(1, recipe.nodes.length - 1);
      const x = fromNode.x + (toNode.x - fromNode.x) * fr;
      const y = fromNode.y + (toNode.y - fromNode.y) * fr;
      return {
        id: newId,
        type: rn.type,
        x: Number.isFinite(x) ? x : 200,
        y: Number.isFinite(y) ? y : 200,
        config: rn.config ? { ...rn.config } : {},
        label: rn.label || rn.type,
      };
    });

    const entryId = idMap.get(recipe.entry);
    const exitId  = idMap.get(recipe.exit);
    if (!entryId || !exitId) {
      console.warn('[applyRecipe] recipe entry/exit ids not present in nodes');
      return null;
    }

    // 2. Build the wiring: replace the original edge with from → entry,
    //    keep all internal recipe edges, and exit → to.
    const recipeEdges = (recipe.edges || []).map((e, i) => ({
      id: `c_${ts + 1000 + i}_${Math.random().toString(36).substr(2, 4)}`,
      from: idMap.get(e.from),
      fromPort: e.fromPort || 'default',
      to: idMap.get(e.to),
      toPort: e.toPort || 'default',
    }));
    const bridgeIn = {
      id: `c_${ts + 9000}_${Math.random().toString(36).substr(2, 4)}`,
      from: conn.from,
      fromPort: conn.fromPort || 'default',
      to: entryId,
      toPort: 'default',
    };
    const bridgeOut = {
      id: `c_${ts + 9001}_${Math.random().toString(36).substr(2, 4)}`,
      from: exitId,
      fromPort: 'default',
      to: conn.to,
      toPort: conn.toPort || 'default',
    };

    // 3. Compose the next state.
    const nextConnections = [
      ...connections.filter((c) => c.id !== target.connectionId),
      bridgeIn,
      ...recipeEdges,
      bridgeOut,
    ];
    const nextNodesRaw = [...nodes, ...newNodes];
    // 4. Single layout pass.
    const nextNodesPositioned = computeLayoutPositions(nextNodesRaw, nextConnections);
    setNodes(nextNodesPositioned);
    setConnections(nextConnections);
    setSelected(entryId);
    setSelectedType('node');
    markDirty();
    return newNodes.map((n) => n.id);
  }, [connections, nodes, computeLayoutPositions, markDirty]);

  // =========================================================================
  // Selection
  // =========================================================================

  const selectNode = useCallback((nodeId) => {
    setSelected(nodeId);
    setSelectedType('node');
    setMultiSelect(new Set());
  }, []);

  const selectConnection = useCallback((connId) => {
    setSelected(connId);
    setSelectedType('connection');
    setMultiSelect(new Set());
  }, []);

  const clearSelection = useCallback(() => {
    setSelected(null);
    setSelectedType(null);
    setMultiSelect(new Set());
  }, []);

  const deleteSelected = useCallback(() => {
    if (!selected) return;
    if (selectedType === 'node') deleteNode(selected);
    else if (selectedType === 'connection') deleteConnection(selected);
  }, [selected, selectedType, deleteNode, deleteConnection]);

  // =========================================================================
  // Drag (node movement)
  // =========================================================================

  const DRAG_THRESHOLD = 4; // px — minimum mouse movement before drag activates
  const dragPendingRef = useRef(null); // { nodeId, startX, startY }
  const draggingRef = useRef(null); // synchronous source of truth for which node is being dragged

  const startDrag = useCallback(
    (nodeId, clientX, clientY) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;
      // Don't activate drag immediately — wait until threshold is exceeded
      dragPendingRef.current = { nodeId, startX: clientX, startY: clientY };
      dragOffsetRef.current = {
        x: (clientX - pan.x) / zoom - node.x,
        y: (clientY - pan.y) / zoom - node.y,
      };
    },
    [nodes, pan, zoom]
  );

  const drag = useCallback(
    (clientX, clientY) => {
      // If drag hasn't activated yet, check threshold
      if (dragPendingRef.current && !draggingRef.current) {
        const dx = clientX - dragPendingRef.current.startX;
        const dy = clientY - dragPendingRef.current.startY;
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        // Threshold exceeded — activate drag synchronously via ref
        draggingRef.current = dragPendingRef.current.nodeId;
        setDragging(draggingRef.current); // also update state for re-render (cursor)
        dragPendingRef.current = null;
      }
      if (!draggingRef.current) return;
      const activeId = draggingRef.current;
      let newX = (clientX - pan.x) / zoom - dragOffsetRef.current.x;
      let newY = (clientY - pan.y) / zoom - dragOffsetRef.current.y;

      // Snap to grid
      const snapped = snapToGrid(newX, newY);
      newX = snapped.x;
      newY = snapped.y;

      // Setting `manualPosition: true` here (not in endDrag) means the very
      // first pixel of movement pins the node — subsequent re-renders triggered
      // mid-drag (e.g. another node being added) won't run computeLayoutPositions
      // and snap the node back under the user's cursor. Persisted on save so
      // pin survives reloads.
      setNodes((prev) =>
        prev.map((n) =>
          n.id === activeId ? { ...n, x: newX, y: newY, manualPosition: true } : n
        )
      );
    },
    [pan, zoom, snapToGrid]
  );

  const endDrag = useCallback(() => {
    dragPendingRef.current = null;
    if (draggingRef.current) markDirty();
    draggingRef.current = null;
    setDragging(null);
  }, [markDirty]);

  // =========================================================================
  // Pan (canvas movement)
  // =========================================================================

  const startPan = useCallback(
    (clientX, clientY) => {
      panRef.current = true;
      panStartRef.current = { x: clientX - pan.x, y: clientY - pan.y };
    },
    [pan]
  );

  const doPan = useCallback((clientX, clientY) => {
    if (!panRef.current) return;
    setPan({
      x: clientX - panStartRef.current.x,
      y: clientY - panStartRef.current.y,
    });
  }, []);

  const endPan = useCallback(() => {
    panRef.current = false;
  }, []);

  // =========================================================================
  // Zoom
  // =========================================================================

  // Conductor OSS-style zoom: 10% steps, range 10%-200%, snaps to clean increments
  const doZoom = useCallback((delta) => {
    setZoom((prev) => {
      // Snap to nearest 10% then step
      const currentPct = Math.round(prev * 10) / 10; // nearest 0.1
      const next = currentPct + delta * 0.1;
      return Math.max(0.1, Math.min(2.0, Math.round(next * 10) / 10));
    });
  }, []);

  const zoomToFit = useCallback(() => {
    if (nodes.length === 0) {
      setPan({ x: 0, y: 0 });
      setZoom(1);
      return;
    }
    // Get actual SVG container size, fallback to reasonable defaults
    const svgEl = svgRef.current;
    const viewW = svgEl?.parentElement?.clientWidth || 800;
    const viewH = svgEl?.parentElement?.clientHeight || 600;

    // Compute bounding box with generous padding
    // P3-A: Include edge labels and comment markers in bounds to prevent Start node clipping
    const padding = 60;
    const xs = nodes.map((n) => n.x);
    const ys = nodes.map((n) => n.y);
    let minX = Math.min(...xs) - padding;
    let minY = Math.min(...ys) - padding;
    let maxX = Math.max(...xs) + NODE_W + padding;
    let maxY = Math.max(...ys) + NODE_H + padding;
    
    // P3-A: Account for edge labels and comment markers that may extend beyond node bounds
    // Edge labels are typically placed at midpoint of edges, which can extend laterally
    // Comment markers (// Marks end of ...) typically add 100+ pixels below last node
    const extraVerticalPadding = 150; // for comment markers below switch/fork structures
    const extraHorizontalPadding = 80;  // for edge labels that fan out horizontally
    maxX += extraHorizontalPadding;
    maxY += extraVerticalPadding;
    minX -= extraHorizontalPadding;
    const w = maxX - minX;
    const h = maxY - minY;

    // Compute scale to fit — NEVER zoom in past 1.0 (100%).
    // For large workflows this zooms out to show everything; for small ones
    // it shows them at natural size centered in the viewport.
    const scaleX = viewW / w;
    const scaleY = viewH / h;
    const fitScale = Math.min(scaleX, scaleY);
    const newZoom = Math.max(0.1, Math.min(1.0, fitScale));

    // Center the content in the viewport
    const contentCenterX = (minX + maxX) / 2;
    const contentCenterY = (minY + maxY) / 2;
    const panX = viewW / 2 - contentCenterX * newZoom;
    const panY = viewH / 2 - contentCenterY * newZoom;

    setPan({ x: panX, y: panY });
    setZoom(newZoom);
  }, [nodes]);

  const resetView = useCallback(() => {
    setPan({ x: 0, y: 0 });
    setZoom(1);
  }, []);

  const setPanZoom = useCallback((newPan, newZoom) => {
    setPan(newPan);
    setZoom(newZoom);
  }, []);

  // =========================================================================
  // Connect Mode (drag-to-connect)
  // =========================================================================

  const startConnect = useCallback((fromId, fromPort, canvasX, canvasY) => {
    // Store origin in canvas coords (fromX/fromY) for the line start point
    // mouseX/mouseY will be updated in screen coords by moveConnect
    setConnecting({
      fromId,
      fromPort,
      fromX: canvasX,
      fromY: canvasY,
      mouseX: canvasX * zoom + pan.x,
      mouseY: canvasY * zoom + pan.y,
    });
  }, [zoom, pan]);

  const moveConnect = useCallback((mouseX, mouseY) => {
    if (!connecting) return;
    setConnecting((prev) => (prev ? { ...prev, mouseX, mouseY } : null));
  }, [connecting]);

  const endConnect = useCallback(
    (toId, toPort) => {
      if (connecting && toId) {
        addConnection(connecting.fromId, connecting.fromPort, toId, toPort || 'default');
      }
      setConnecting(null);
    },
    [connecting, addConnection]
  );

  const cancelConnect = useCallback(() => {
    setConnecting(null);
  }, []);

  // =========================================================================
  // Serialization (load/save canvas)
  // =========================================================================

  const getCanvasData = useCallback(
    () => ({
      nodes,
      connections,
      dataFlow: Array.from(dataFlow.entries())
        .filter(([_, flow]) => flow && (flow.sourceVar || flow.targetInput)) // skip empty/malformed
        .map(([connId, flow]) => ({
          connId,
          sourceVar: flow.sourceVar || '',
          targetInput: flow.targetInput || '',
        })),
      // RC-CanvasV3-ProtectManifest — include the manifest in the
      // payload so it gets persisted (BE definition is JSONB,
      // unknown top-level keys pass through). Null is preserved as
      // null so a cleared manifest (post-Unprotect) overwrites the
      // stored one.
      protectManifest,
    }),
    [nodes, connections, dataFlow, protectManifest]
  );

  /**
   * loadCanvasData — loads nodes/connections and optionally auto-layouts them.
   * When autoLayoutOnLoad is true, layout is computed BEFORE setting state
   * (avoids stale-closure timing issues with React's async setState).
   */
  const loadCanvasData = useCallback(async (data, { autoLayoutOnLoad = false, zoomToFitOnLoad = false, changeKind = null } = {}) => {
    /* RC-117c — Optional changeKind hint for the NEXT save that follows
     * this load. Set by callers that know the load is being driven by
     * a non-manual source (AI Build, AI Fix, Import). The save callback
     * reads pendingChangeKindRef.current and clears it after persisting.
     * Default null means the next save will fall back to "manual_save",
     * preserving the pre-RC-117c narrative for operator-driven edits. */
    if (changeKind && typeof changeKind === 'string') {
      pendingChangeKindRef.current = changeKind;
    }
    // --- Normalize connection field names (source/target → from/to) ---
    // Filter out malformed connections that have no from/to identifiers
    const rawConnections = (data?.connections || [])
      .filter((c) => (c.from || c.source) && (c.to || c.target))
      .map((c) => ({
        id: c.id || `c_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
        from: c.from || c.source,
        fromPort: c.fromPort || c.sourcePort || 'default',
        to: c.to || c.target,
        toPort: c.toPort || c.targetPort || 'default',
      }));

    if (process.env.NODE_ENV !== 'production') {
      const rawLen = data?.connections?.length || 0;
      console.log(`[loadCanvasData] Raw connections: ${rawLen}, After normalization: ${rawConnections.length}`);
      if (rawConnections.length < rawLen) {
        console.warn(`[loadCanvasData] ${rawLen - rawConnections.length} connections dropped during normalization (missing from/to)`);
      }
    }

    // --- DAG Validation & Repair (runs BEFORE layout or state update) ---
    const dagResult = validateAndRepairDAG(data?.nodes || [], rawConnections);
    if (dagResult.repairs.length > 0) {
      console.warn('[useCanvas] DAG repairs applied:', dagResult.repairs);
      // P1-B: surface the repair list to the UI. Captured even on success
      // path so the user sees what changed; cleared on the next clean load.
      setOrphanRepairReport({
        count: dagResult.repairs.length,
        repairs: dagResult.repairs.slice(0, 50), // safety cap so a runaway never produces huge state
        appliedAt: new Date().toISOString(),
      });
    } else {
      // Clear any prior report on a clean load.
      setOrphanRepairReport(null);
    }

    let loadedNodes = dagResult.nodes;
    const loadedConnections = dagResult.connections;

    // ── JSON Validation (pre-render structural check) ──
    // Log Canvas JSON integrity BEFORE layout or rendering — this is the
    // JSON-first validation step: inspect structure before committing to Canvas.
    if (process.env.NODE_ENV !== 'production') {
      const validation = validateCanvasJSON({ nodes: loadedNodes, connections: loadedConnections });
      if (!validation.valid) {
        console.error('[loadCanvasData] ⛔ Canvas JSON validation FAILED:', validation.issues);
      } else {
        console.log('[loadCanvasData] ✅ Canvas JSON valid —', validation.metrics);
      }
      if (validation.issues.length > 0) {
        console.table(validation.issues);
      }
    }

    // ── CRITICAL: Strip stale ELK bend points from persisted connections ──
    // Prior builds serialized bendPoints/labelPosition onto connections. Those
    // absolute coordinates reference a layout that no longer exists, so they
    // make every saved workflow render with edges streaming off to nowhere.
    // Always drop them before layout — fresh layout writes fresh bend points.
    for (let i = 0; i < loadedConnections.length; i++) {
      const c = loadedConnections[i];
      if (c.bendPoints !== undefined || c.labelPosition !== undefined) {
        const { bendPoints: _bp, labelPosition: _lp, ...rest } = c;
        loadedConnections[i] = rest;
      }
    }

    // ── Layout engine selection ──
    // Sugiyama (hand-rolled layered DAG in dagLayout.js) is the default stable
    // engine. ELK is opt-in via localStorage until its FORK/JOIN-aware
    // partitioning is wired up. Flip in DevTools Console:
    //     localStorage.setItem('canvas.useElk', 'true'); location.reload();
    const useElk = (typeof window !== 'undefined')
      && window.localStorage?.getItem('canvas.useElk') === 'true';

    if (autoLayoutOnLoad && loadedNodes.length > 0) {
      const totalNodes = loadedNodes.length;
      const rankSep = totalNodes <= 8 ? 140 : totalNodes <= 12 ? 100 : totalNodes <= 20 ? 80 : 60;
      const nodeSep = totalNodes <= 8 ? 80 : totalNodes <= 12 ? 60 : totalNodes <= 20 ? 50 : 40;

      // Snapshot the saved (manualPosition, x, y) for every node BEFORE the
      // layout engine rewrites positions. After layout finishes (any engine),
      // we restore the saved coords for every pinned node so user choices
      // survive a workflow reload. See P0-A in CANVAS_PRODUCTION_AUDIT.md.
      const pinSnapshot = new Map();
      for (const n of loadedNodes) {
        if (n && n.manualPosition === true && Number.isFinite(n.x) && Number.isFinite(n.y)) {
          pinSnapshot.set(n.id, { x: n.x, y: n.y });
        }
      }
      const restorePins = (nodes) => nodes.map((n) => {
        const pin = pinSnapshot.get(n.id);
        if (!pin) return n;
        return { ...n, x: pin.x, y: pin.y, manualPosition: true };
      });

      if (useElk) {
        console.log(`%c[loadCanvasData] Running ELK layered layout on ${totalNodes} nodes, ${loadedConnections.length} connections`, 'color: #00cc00; font-weight: bold');
        try {
          const { nodes: positioned, edges: routedEdges } = await elkLayoutDAG(
            loadedNodes, loadedConnections,
            { nodeWidth: NODE_W, nodeHeight: NODE_H, rankSep, nodeSep, padding: 80, rankDir: 'TB' }
          );
          loadedNodes = positioned;
          const bendMap = new Map(routedEdges.map((e) => [`${e.from}__${e.to}`, e]));
          for (let i = 0; i < loadedConnections.length; i++) {
            const c = loadedConnections[i];
            const routed = bendMap.get(`${c.from}__${c.to}`);
            if (routed) {
              loadedConnections[i] = { ...c, bendPoints: routed.bendPoints, labelPosition: routed.labelPosition };
            }
          }
        } catch (err) {
          console.error('[loadCanvasData] ELK failed — falling back to Sugiyama', err);
          try {
            loadedNodes = runLayout(loadedNodes, loadedConnections, { nodeWidth: NODE_W, nodeHeight: NODE_H, rankSep, nodeSep, padding: 80, rankDir: 'TB' }, irLaidRef);
          } catch (e2) {
            console.error('[loadCanvasData] Sugiyama also failed — using backend positions', e2);
            loadedNodes = dagResult.nodes;
          }
        }
      } else {
        // Default: stable Sugiyama. Long edges are drawn as clean diagonals.
        // Optional bend-point threading (localStorage `canvas.longEdgeBends`)
        // routes through dummy positions — useful for debugging, opt-in until
        // Phase 1b trunk anchoring makes straight diagonals reliably clean.
        try {
          loadedNodes = runLayout(loadedNodes, loadedConnections, {
            nodeWidth: NODE_W, nodeHeight: NODE_H, rankSep, nodeSep, padding: 80, rankDir: 'TB',
          }, irLaidRef);

          // P3-A Bug #4, #6: Post-layout passes
          loadedNodes = anchorCommentMarkers(loadedNodes, loadedConnections);
          loadedNodes = alignJoinInboundBranches(loadedNodes, loadedConnections);
          // §121e Gap C: pin JOIN/convergence x to matching splitter's x so
          // uneven-branch subgraphs don't tilt the trunk off-center.
          loadedNodes = pinJoinsToSplitterTrunk(loadedNodes, loadedConnections);

          const useLongEdgeBends = window.localStorage?.getItem('canvas.longEdgeBends') === 'true';
          const bendPointsMap = loadedNodes.__bendPoints;
          if (useLongEdgeBends && bendPointsMap && bendPointsMap.size > 0) {
            for (let i = 0; i < loadedConnections.length; i++) {
              const c = loadedConnections[i];
              const bps = bendPointsMap.get(`${c.from}__${c.to}`);
              if (bps) loadedConnections[i] = { ...c, bendPoints: bps };
            }
          }
        } catch (err) {
          console.error('[loadCanvasData] Sugiyama layout failed — using backend positions', err);
          loadedNodes = dagResult.nodes;
        }
      }

      // Restore user-pinned coordinates after whichever layout engine ran.
      // Done last so trunk-anchoring / comment-marker passes (which assume
      // the layout-engine grid) finish first, then user pins win at render.
      if (pinSnapshot.size > 0) {
        loadedNodes = restorePins(loadedNodes);
      }
    } else {
      console.log(`[loadCanvasData] Skipping auto-layout (autoLayoutOnLoad=${autoLayoutOnLoad}, nodeCount=${loadedNodes.length})`);
    }

    // Sanitize node positions — ensure every node has valid finite x/y to prevent
    // NaN propagation through port calculations and SVG rendering
    loadedNodes = loadedNodes.map((n) => {
      if (!Number.isFinite(n.x) || !Number.isFinite(n.y)) {
        console.warn(`[useCanvas] Node "${n.id}" (${n.type}) has invalid position (x=${n.x}, y=${n.y}), assigning default`);
        return { ...n, x: Number.isFinite(n.x) ? n.x : 200, y: Number.isFinite(n.y) ? n.y : 200 };
      }
      return n;
    });

    // --- Compute zoomToFit pan/zoom INLINE using local variables (avoids stale closure) ---
    if (zoomToFitOnLoad && loadedNodes.length > 0) {
      const svgEl = svgRef.current;
      const viewW = svgEl?.parentElement?.clientWidth || 800;
      const viewH = svgEl?.parentElement?.clientHeight || 600;
      const pad = 60;
      const xs = loadedNodes.map((n) => n.x);
      const ys = loadedNodes.map((n) => n.y);
      const minX = Math.min(...xs) - pad;
      const minY = Math.min(...ys) - pad;
      const maxX = Math.max(...xs) + NODE_W + pad;
      const maxY = Math.max(...ys) + NODE_H + pad;
      const w = maxX - minX;
      const h = maxY - minY;
      const scaleX = viewW / w;
      const scaleY = viewH / h;
      const newZoom = Math.max(0.1, Math.min(1.0, Math.min(scaleX, scaleY)));
      const contentCenterX = (minX + maxX) / 2;
      const contentCenterY = (minY + maxY) / 2;
      const panX = viewW / 2 - contentCenterX * newZoom;
      const panY = viewH / 2 - contentCenterY * newZoom;
      setPan({ x: panX, y: panY });
      setZoom(newZoom);
    }

    // RC-118 — reset undo/redo history BEFORE we lay the new state down,
    // so the auto-commit useEffect treats the freshly-loaded workflow as
    // the new baseline. Without this, opening a new workflow would push
    // its load as a "mutation" on top of the previous workflow's history,
    // and Cmd+Z would unload the workflow back to the prior one's state.
    resetHistory();
    setNodes(loadedNodes);
    setConnections(loadedConnections);
    // RC-CanvasV3-ProtectManifest — adopt the manifest from the loaded
    // workflow definition. `null`/undefined explicitly resets to null
    // so a freshly-cleared canvas doesn't keep a stale manifest from
    // the previous workflow.
    setProtectManifest(data?.protectManifest || null);
    // Always reset dataFlow when switching workflows — stale entries from
    // the previous workflow must not leak into the new one
    const flowMap = new Map();
    if (data?.dataFlow) {
      data.dataFlow.forEach(({ connId, sourceVar, targetInput }) => {
        if (sourceVar || targetInput) { // skip empty/malformed entries
          flowMap.set(connId, { sourceVar, targetInput, dataPreview: null });
        }
      });
    }
    setDataFlow(flowMap);
    setDirty(false);
  }, [resetHistory]);

  const save = useCallback(async () => {
    if (onSave) {
      await onSave(getCanvasData());
      setDirty(false);
    }
  }, [onSave, getCanvasData]);

  /**
   * Save explicit data to the backend, bypassing getCanvasData().
   * Use this when you've just called loadCanvasData() and React hasn't
   * re-rendered yet — getCanvasData() would still return stale state.
   */
  const saveData = useCallback(async (data) => {
    if (onSave && data) {
      await onSave(data);
      setDirty(false);
    }
  }, [onSave]);

  // =========================================================================
  // Coordinate Helpers
  // =========================================================================

  const screenToCanvas = useCallback(
    (screenX, screenY) => ({
      x: (screenX - pan.x) / zoom,
      y: (screenY - pan.y) / zoom,
    }),
    [pan, zoom]
  );

  const getNodeCenter = useCallback((nodeId) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return { x: 0, y: 0 };
    return { x: node.x + NODE_W / 2, y: node.y + NODE_H / 2 };
  }, [nodes]);

  return {
    // Core State
    nodes,
    connections,
    selected,
    selectedType,
    multiSelect,
    dragging,
    connecting,
    pan,
    zoom,
    dirty,
    clearDirty: useCallback(() => setDirty(false), []),
    svgRef,
    executionState,
    dagValidation,
    dataFlow,
    // RC-CanvasV3-ProtectManifest — expose the manifest + setter so
    // Protect can write it after auto-insertion and Unprotect can
    // read it for exact-replay.
    protectManifest,
    setProtectManifest,

    // Selection
    selectNode,
    selectConnection,
    clearSelection,
    deleteSelected,
    toggleMultiSelect,
    selectRegion,

    // Node ops
    addNode,
    updateNode,
    deleteNode,

    // Connection ops
    addConnection,
    deleteConnection,
    canConnect,
    insertNodeOnConnection,
    // Phase 3 additions (CANVAS_PICKER_INLINE_DESIGN.md §3.2)
    appendNodeAfter,
    appendBranchTerminator,
    // Phase 4 (CANVAS_PICKER_INLINE_DESIGN.md §5)
    applyRecipe,
    // W130 — single primitive for user-initiated task insertion. All
    // user-facing palette / picker / drop call sites in CanvasPageV3 MUST
    // route through this — never call addNode + insertNodeOnConnection
    // separately at the call site. See in-source docs for the decision
    // tree and orphan-prevention contract.
    addTaskToFlow,

    // Drag
    startDrag,
    drag,
    endDrag,
    get dragPending() { return dragPendingRef.current !== null; },

    // Pan
    startPan,
    doPan,
    endPan,

    // Zoom
    doZoom,
    zoomToFit,
    resetView,
    setPanZoom,

    // Connect mode
    startConnect,
    moveConnect,
    endConnect,
    cancelConnect,

    // DAG Operations
    validateDAG,
    topologicalSort,
    getExecutionPlan,

    // P1-B: orphan / repair report
    orphanRepairReport,
    dismissOrphanRepairReport: () => setOrphanRepairReport(null),

    // Port Management
    getNodePorts,
    getPortPosition,
    getNodePortConfig,

    // Connection Validation
    canConnect,

    // Execution State
    setNodeExecutionState,
    setExecutionStates,
    clearExecutionState,
    getRunningNodes,
    getExecutionProgress,

    // Multi-Select
    moveSelected,
    deleteSelectedNodes,

    // Grid & Layout
    snapToGrid,
    autoLayout,

    // Data Flow
    updateConnectionDataFlow,
    getNodeInputData,
    getNodeOutputSchema,

    // Serialization
    getCanvasData,
    loadCanvasData,
    save,
    saveData,

    // RC-117c — Narrative hint for the next save's change_kind. AI Build /
    // AI Fix / Import paths call setPendingChangeKind('ai_build' | 'ai_fix'
    // | 'import') before invoking loadCanvasData (or pass changeKind in
    // the loadCanvasData options bag). The save handler in CanvasPageV3
    // calls consumePendingChangeKind() right before posting to the BE so
    // the hint flows through saveCanvasApi → controller → service →
    // WorkflowVersion.changeKind. Ref-based + one-shot so a stale hint
    // never leaks to an unrelated subsequent save.
    setPendingChangeKind,
    consumePendingChangeKind,

    // RC-118 — Undo / Redo. canUndo + canRedo drive toolbar button
    // enabled-state; undo() / redo() are wired to Cmd+Z / Cmd+Shift+Z in
    // CanvasPageV3. resetHistory is exposed for callers that swap the
    // canvas state outside the load path (rare; loadCanvasData already
    // calls it internally).
    undo,
    redo,
    canUndo,
    canRedo,
    resetHistory,
    // RC-118b — per-op labels for toolbar tooltips. Empty string when
    // the next undo/redo target has no derived label (baseline / replay
    // restore); CanvasPageV3 toolbar falls back to "Undo" / "Redo" plain.
    undoLabel,
    redoLabel,
    // RC-118b — used by callers to scope the localStorage persistence
    // key to the active workflow. CanvasPageV3 passes selectedWorkflowId
    // here so undo can survive reloads without leaking history across
    // workflows.
    rehydrateHistoryFor,
    persistHistoryFor,

    // RC-135 — Autosave helpers. CanvasPageV3 runs the 30s cadence and
    // the restore-on-load banner; this hook provides the storage
    // primitives so the snapshot read/write surface stays inside the
    // canvas state owner (which has `nodes`/`connections` in scope and
    // doesn't have to re-pluck them from a stale closure).
    //   persistUnsavedFor(id, baseVersion) — write current snapshot
    //   readUnsavedFor(id)                 — read or null
    //   clearUnsavedFor(id)                — remove on save-success / discard
    persistUnsavedFor,
    readUnsavedFor,
    clearUnsavedFor,

    // Direct state setters (used for swapping design ↔ execution DAG snapshots)
    setNodes,
    setConnections,

    // Helpers
    screenToCanvas,
    getNodeCenter,

    // IR pipeline (step 8.4) — ref to the most recent laid-out IR tree,
    // consumed by useIRHeightFeedback in CanvasPage.jsx. No-op when the
    // IR pipeline flag is off (ref stays null).
    irLaidRef,
  };
}
